import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { Link } from "react-router-dom";
import {
  Users, Store, Package, Radio, ShoppingBag, Shield, Sparkles, Wallet, TrendingUp,
  Lock, Unlock, Save, Truck, Undo2, Tag,
  UserCheck, CreditCard, Music, Image as ImageIcon, Megaphone,
  Plus, Trash2, Pencil, X, Check, DollarSign, Clock, PlayCircle, Pause,
  Upload,
  KeyRound, ShieldCheck, Layout as LayoutIcon, ArrowUp, ArrowDown
} from "lucide-react";
import CouponsManager from "../components/CouponsManager";
import BulkUploadModal from "../components/BulkUploadModal";
import AdminWholesaleTab from "../components/AdminWholesaleTab";
import AdminRentalsTab from "../components/AdminRentalsTab";

// ─── Bulk-upload field schemas (re-used across Ads / Backgrounds / Music) ───
const ADS_BULK = {
  title: "Bulk upload ads",
  endpoint: "/admin/ads/bulk",
  payloadKey: "ads",
  csvHeaders: [
    { key: "title", label: "Title", required: true },
    { key: "kind", label: "Kind", hint: "banner | video", required: true },
    { key: "placement", label: "Placement",
      hint: "home_top | home_mid | search_top | feed_interstitial | category_top",
      required: true },
    { key: "media_url", label: "Media URL", required: true },
    { key: "thumbnail_url", label: "Thumb URL" },
    { key: "link_url", label: "Click URL" },
    { key: "sponsor_name", label: "Sponsor" },
    { key: "start_at", label: "Start", hint: "ISO 8601 e.g. 2026-04-01T00:00:00Z" },
    { key: "end_at", label: "End", hint: "ISO 8601" },
    { key: "active", label: "Active", hint: "true | false" },
  ],
  sampleCsv: `title,kind,placement,media_url,link_url,sponsor_name,start_at,end_at,active
Spring Sale,banner,home_top,https://example.com/spring.jpg,/category/cat_fashion,ShopRizz,2026-04-01T00:00:00Z,2026-04-30T23:59:59Z,true
Live Tonight,video,home_mid,https://example.com/live.mp4,,Studio,,,true`,
  sampleJson: `[
  {"title":"Spring Sale","kind":"banner","placement":"home_top",
   "media_url":"https://example.com/spring.jpg","link_url":"/category/cat_fashion",
   "sponsor_name":"ShopRizz","start_at":"2026-04-01T00:00:00Z",
   "end_at":"2026-04-30T23:59:59Z","active":true}
]`,
};

const BGS_BULK = {
  title: "Bulk upload backgrounds",
  endpoint: "/admin/studio/backgrounds/bulk",
  payloadKey: "backgrounds",
  csvHeaders: [
    { key: "name", label: "Name", required: true },
    { key: "kind", label: "Kind", hint: "gradient | image | solid", required: true },
    { key: "value", label: "Value",
      hint: "CSS gradient string, image URL, or hex color", required: true },
    { key: "active", label: "Active", hint: "true | false" },
  ],
  sampleCsv: `name,kind,value,active
Aurora,gradient,"linear-gradient(135deg,#00F0FF,#9B5DE5)",true
Beach,image,https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1200,true
Charcoal,solid,#1A1A1F,true`,
  sampleJson: `[
  {"name":"Aurora","kind":"gradient","value":"linear-gradient(135deg,#00F0FF,#9B5DE5)","active":true},
  {"name":"Beach","kind":"image","value":"https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1200","active":true}
]`,
};

const MUSIC_BULK = {
  title: "Bulk upload music",
  endpoint: "/admin/studio/music/bulk",
  payloadKey: "tracks",
  csvHeaders: [
    { key: "title", label: "Title", required: true },
    { key: "url", label: "Audio URL", required: true },
    { key: "artist", label: "Artist" },
    { key: "genre", label: "Genre" },
    { key: "bpm", label: "BPM", hint: "number" },
    { key: "duration", label: "Duration (s)", hint: "number" },
    { key: "cover", label: "Cover URL" },
    { key: "active", label: "Active", hint: "true | false" },
  ],
  sampleCsv: `title,artist,genre,bpm,duration,cover,url,active
Neon Drive,Volt,EDM,128,30,https://images.unsplash.com/photo-1519892300165-cb5542fb47c7?w=300,https://cdn.pixabay.com/download/audio/x.mp3,true
Lo-Fi Walk,Kō,Lo-Fi,85,30,,https://cdn.pixabay.com/download/audio/y.mp3,true`,
  sampleJson: `[
  {"title":"Neon Drive","artist":"Volt","genre":"EDM","bpm":128,"duration":30,
   "url":"https://cdn.pixabay.com/download/audio/x.mp3","active":true}
]`,
};


export default function AdminPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [lockable, setLockable] = useState([]);
  const [adminCats, setAdminCats] = useState([]);
  const [adminStores, setAdminStores] = useState([]);
  const [adminProducts, setAdminProducts] = useState([]);
  const [tab, setTab] = useState("dashboard");
  const [msg, setMsg] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [s, u, o, c, st, p] = await Promise.all([
          api.get("/admin/stats"),
          api.get("/admin/users"),
          api.get("/admin/orders"),
          api.get("/categories"),
          api.get("/stores"),
          api.get("/products?limit=100"),
        ]);
        setStats(s.data); setUsers(u.data); setOrders(o.data);
        setAdminCats(c.data); setAdminStores(st.data); setAdminProducts(p.data);
      } catch {}
    })();
  }, []);

  const loadSellers = async () => {
    try {
      const [se, lf] = await Promise.all([
        api.get("/admin/sellers"),
        api.get("/admin/lockable-fields"),
      ]);
      setSellers(se.data);
      setLockable(lf.data.lockable_fields);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { if (tab === "sellers") loadSellers(); }, [tab]);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(""), 2000); };

  const updateLocks = async (sellerId, locked_fields) => {
    try {
      await api.put(`/admin/sellers/${sellerId}/locks`, { locked_fields });
      flash("Locks updated.");
      loadSellers();
    } catch (e) { alert(formatApiError(e?.response?.data?.detail) || "Failed"); }
  };

  // Reset tab if the current user no longer has access (must run unconditionally)
  useEffect(() => {
    if (!user || user.role !== "admin") return;
    const isSuperU = !!user.is_super_admin || user.email === "admin@shoprizz.com";
    const perms = new Set(user.permissions || []);
    const allowed = (p) => isSuperU || (p !== "__super__" && perms.has(p));
    const allKeys = ["dashboard.view","approvals.manage","subscriptions.manage","plans.manage",
      "sellers.manage","coupons.manage","announcements.manage","ads.manage",
      "experiments.manage","studio.manage","users.view","orders.view","products.manage"];
    const tabPerm = {dashboard:"dashboard.view", approvals:"approvals.manage", subs:"subscriptions.manage",
      plans:"plans.manage", payments:"subscriptions.manage", fields:"users.view",
      sellers:"sellers.manage", coupons:"coupons.manage",
      home:"home.manage",
      ptypes:"products.manage",
      announcements:"announcements.manage", ads:"ads.manage", experiments:"experiments.manage",
      studio:"studio.manage", users:"users.view", orders:"orders.view", subadmins:"__super__"};
    if (tab in tabPerm && !allowed(tabPerm[tab])) {
      const first = Object.keys(tabPerm).find(k => allowed(tabPerm[k]));
      if (first) setTab(first);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Deep-link via hash: /admin#rentals, /admin#wholesale, /admin#orders, etc.
  // Reacts to BOTH cold-load (URL hash set on initial render) AND in-app
  // navigation that mutates window.location.hash without a remount.
  useEffect(() => {
    const applyHash = () => {
      const h = (window.location.hash || "").replace(/^#/, "").split("/")[0];
      if (h) setTab(h);
    };
    applyHash();
    window.addEventListener("hashchange", applyHash);
    return () => window.removeEventListener("hashchange", applyHash);
  }, []);

  if (!user || user.role !== "admin") {
    return <div className="max-w-xl mx-auto p-10 text-center">
      <Shield size={36} className="mx-auto text-[#FF007F] mb-3" />
      <div className="font-display text-2xl">Admin access required</div>
      <Link to="/login" className="btn-primary mt-4 inline-block">Login as admin</Link>
    </div>;
  }

  const TABS_ALL = [
    ["dashboard", "Overview", "dashboard.view"],
    ["approvals", "Approvals", "approvals.manage"],
    ["subs", "Subscriptions", "subscriptions.manage"],
    ["plans", "Plans", "plans.manage"],
    ["payments", "Payments", "subscriptions.manage"],
    ["wholesale", "Wholesale", "sellers.manage"],
    ["rentals", "Rentals", "sellers.manage"],
    ["fields", "Profile fields", "users.view"],
    ["sellers", "Sellers", "sellers.manage"],
    ["coupons", "Coupons", "coupons.manage"],
    ["home", "Home page", "home.manage"],
    ["ptypes", "Product types", "products.manage"],
    ["announcements", "Announcements", "announcements.manage"],
    ["ads", "Ads", "ads.manage"],
    ["experiments", "A/B", "experiments.manage"],
    ["studio", "Studio assets", "studio.manage"],
    ["users", "Users", "users.view"],
    ["orders", "Orders", "orders.view"],
    ["subadmins", "Sub-admins", "__super__"],     // only for super-admin
  ];
  const isSuper = !!user.is_super_admin || user.email === "admin@shoprizz.com";
  const myPerms = new Set(user.permissions || []);
  const hasPerm = (p) => isSuper || (p === "__super__" ? false : myPerms.has(p));
  const TABS = TABS_ALL.filter(([, , perm]) => isSuper || hasPerm(perm));

  return (
    <div className="pb-24 md:pb-12 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
      <h1 className="font-display font-black text-4xl sm:text-6xl uppercase mb-6">Admin<span className="text-[#FF007F]">.</span></h1>

      <div className="lg:grid lg:grid-cols-[220px_1fr] lg:gap-6 lg:items-start">
        {/* Mobile / tablet — horizontal pill strip */}
        <div className="lg:hidden sticky top-[64px] z-30 -mx-4 px-4 bg-[#0F0817]/95 backdrop-blur border-b border-[#3D2845] mb-4 relative">
          <div className="flex gap-1 overflow-x-auto no-scrollbar">
            {TABS.map(([k, l]) => {
              const isSubAdmins = k === "subadmins";
              const active = tab === k;
              return (
                <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
                  className={`shrink-0 px-4 py-3 text-sm font-bold uppercase tracking-wider border-b-2 transition flex items-center gap-1.5
                    ${active
                      ? "border-[#FF007F] text-[#FF007F]"
                      : isSubAdmins
                        ? "border-transparent text-[#D4FF00] hover:text-[#FAFAFA]"
                        : "border-transparent text-[#A1A1AA] hover:text-[#FAFAFA]"}`}>
                  {isSubAdmins && <ShieldCheck size={12} className="inline"/>}
                  {l}
                </button>
              );
            })}
          </div>
          <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-[#0F0817] to-transparent"/>
        </div>

        {/* Desktop — vertical left rail, sticky so it follows the page */}
        <aside className="hidden lg:block sticky top-[88px] self-start" data-testid="admin-sidebar">
          <nav className="flex flex-col gap-1 bg-[#261534] border border-[#3D2845] rounded-2xl p-2">
            {TABS.map(([k, l]) => {
              const isSubAdmins = k === "subadmins";
              const active = tab === k;
              return (
                <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
                  className={`w-full text-left px-3 py-2 rounded-xl text-sm font-bold uppercase tracking-wider transition flex items-center gap-2 ${
                    active
                      ? "bg-[#FF007F]/10 text-[#FF007F] border-l-2 border-[#FF007F] pl-[10px]"
                      : isSubAdmins
                        ? "text-[#D4FF00] hover:bg-[#3D2845]"
                        : "text-[#A1A1AA] hover:text-[#FAFAFA] hover:bg-[#3D2845]"
                  }`}>
                  {isSubAdmins && <ShieldCheck size={12}/>}
                  <span className="truncate">{l}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        <div className="min-w-0">

      {msg && <div className="mb-4 text-sm text-[#D4FF00]" data-testid="admin-msg">{msg}</div>}

      {tab === "dashboard" && stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi icon={Users} label="Users" value={stats.users} />
          <Kpi icon={Store} label="Stores" value={stats.stores} />
          <Kpi icon={Package} label="Products" value={stats.products} />
          <Kpi icon={Radio} label="Live now" value={stats.live_now} color="#FF007F" />
          <Kpi icon={ShoppingBag} label="Orders" value={stats.orders} color="#D4FF00" />
          <Kpi icon={ShoppingBag} label="GMV" value={`$${stats.gmv ?? 0}`} color="#D4FF00" />
          <Kpi icon={ShoppingBag} label="Refunded" value={`$${stats.refunded ?? 0}`} color="#FF007F" />
          <Kpi icon={Sparkles} label="Rewards paid" value={`$${stats.rewards_paid ?? 0}`} color="#00F0FF" />
          <Kpi icon={Wallet} label="Wallet held" value={`$${stats.wallet_outstanding ?? 0}`} />
          <Kpi icon={Radio} label="Streams total" value={stats.streams} />
          <Kpi icon={Package} label="Campaigns" value={stats.campaigns} />
          <Kpi icon={TrendingUp} label="Platform fee" value={`${stats.platform_fee_pct || 15}%`} />
        </div>
      )}

      {tab === "sellers" && (
        <SellersTab sellers={sellers} lockable={lockable} onSave={updateLocks} />
      )}

      {tab === "coupons" && (
        <CouponsManager scope="admin" stores={adminStores} categories={adminCats} products={adminProducts}/>
      )}

      {tab === "users" && (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#3D2845] text-left">
              <tr>
                <th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Role</th><th className="p-3">ID</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.user_id} className="border-t border-[#3D2845]">
                  <td className="p-3 font-bold">{u.name}</td>
                  <td className="p-3">{u.email}</td>
                  <td className="p-3"><span className={`chip ${u.role === "admin" ? "active" : ""}`}>{u.role}</span></td>
                  <td className="p-3 font-mono-display text-xs text-[#A1A1AA]">{u.user_id}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "orders" && (
        <AdminOrdersTab orders={orders} onReload={async () => {
          try { const { data } = await api.get("/admin/orders"); setOrders(data); } catch {}
        }} />
      )}

      {tab === "approvals" && <AdminApprovalsTab flash={flash}/>}
      {tab === "subs" && <AdminSubscriptionsTab flash={flash}/>}
      {tab === "plans" && <AdminPlansTab flash={flash}/>}
      {tab === "payments" && <AdminPaymentsTab flash={flash}/>}
      {tab === "wholesale" && <AdminWholesaleTab flash={flash}/>}
      {tab === "rentals" && <AdminRentalsTab flash={flash}/>}
      {tab === "fields" && <AdminFieldsTab flash={flash}/>}
      {tab === "announcements" && <AdminAnnouncementsTab flash={flash}/>}
      {tab === "ads" && <AdminAdsTab flash={flash}/>}
      {tab === "experiments" && <AdminExperimentsTab flash={flash}/>}
      {tab === "studio" && <AdminStudioAssetsTab flash={flash}/>}
      {tab === "home" && <AdminHomeTab flash={flash}/>}
      {tab === "ptypes" && <AdminProductTypesTab flash={flash}/>}
      {tab === "subadmins" && <AdminSubAdminsTab flash={flash} currentUser={user}/>}
        </div>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, color = "#FAFAFA" }) {
  return (
    <div className="card p-4">
      <Icon size={16} strokeWidth={2.5} style={{ color }} />
      <div className="text-xs text-[#A1A1AA] uppercase tracking-wider font-bold mt-2">{label}</div>
      <div className="font-display font-black text-3xl">{value}</div>
    </div>
  );
}

function SellersTab({ sellers, lockable, onSave }) {
  return (
    <div className="space-y-3">
      <div className="card p-4">
        <div className="font-display font-bold uppercase text-sm">Seller field locks</div>
        <div className="text-xs text-[#A1A1AA] mt-1">
          Toggle a field to lock/unlock it for the seller. Locked fields are read-only on the seller's profile.
        </div>
      </div>

      {sellers.length === 0 && (
        <div className="card p-8 text-center text-[#A1A1AA]">No sellers yet.</div>
      )}

      {sellers.map(s => (
        <SellerLockCard key={s.user_id} seller={s} lockable={lockable} onSave={onSave} />
      ))}
    </div>
  );
}

function SellerLockCard({ seller, lockable, onSave }) {
  const [selected, setSelected] = useState(new Set(seller.locked_fields || []));
  const [dirty, setDirty] = useState(false);
  const [details, setDetails] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [busy, setBusy] = useState(false);

  const toggle = (field) => {
    const next = new Set(selected);
    next.has(field) ? next.delete(field) : next.add(field);
    setSelected(next);
    setDirty(true);
  };

  const save = () => {
    onSave(seller.user_id, [...selected]);
    setDirty(false);
  };

  const loadDetails = async () => {
    if (details) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/sellers/${seller.user_id}/details`);
      setDetails(data);
    } catch {} finally { setBusy(false); }
  };

  const expand = async () => {
    setShowDetails(s => !s);
    if (!showDetails) await loadDetails();
  };

  const toggleActive = async () => {
    const live = (details?.user?.seller_status === "approved");
    const verb = live ? "deactivate" : "activate";
    let note = "";
    if (live) {
      note = prompt(`Reason to deactivate ${seller.name}? (visible to seller)`, "");
      if (note === null) return;
    } else if (!window.confirm(`Reactivate ${seller.name}?`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/sellers/${seller.user_id}/active`,
        { active: !live, note });
      // Reload details
      const { data } = await api.get(`/admin/sellers/${seller.user_id}/details`);
      setDetails(data);
    } catch (e) { alert(formatApiError(e?.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  const status = details?.user?.seller_status || seller.role;
  const statusColor = status === "approved" ? "#D4FF00" :
                      status === "pending"  ? "#00F0FF" :
                      status === "suspended" ? "#FF007F" : "#FAFAFA";

  return (
    <div className="card p-4" data-testid={`admin-seller-${seller.user_id}`}>
      <div className="flex items-start gap-3 mb-3">
        <img src={seller.picture} className="w-12 h-12 rounded-full" alt="" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-bold">{seller.name}</div>
            <span className="chip text-[10px]"
              style={{ background: `${statusColor}22`, color: statusColor, border: `1px solid ${statusColor}55` }}
              data-testid={`admin-seller-status-${seller.user_id}`}>{status}</span>
          </div>
          <div className="text-xs text-[#A1A1AA] truncate">{seller.email} · {seller.role}</div>
          {seller.store && (
            <div className="text-xs text-[#D4FF00] mt-1">Store: {seller.store.name}</div>
          )}
        </div>
        <div className="flex gap-2 shrink-0">
          <button onClick={expand} className="btn-secondary text-xs px-3 py-1.5"
            data-testid={`admin-seller-toggle-${seller.user_id}`}>
            {showDetails ? "Hide details" : "View details"}
          </button>
          <button onClick={async () => { await loadDetails(); toggleActive(); }}
            disabled={busy}
            data-testid={`admin-seller-active-${seller.user_id}`}
            className={`text-xs px-3 py-1.5 rounded-full border transition ${
              status === "approved"
                ? "bg-[#FF007F]/15 text-[#FF007F] border-[#FF007F]/40 hover:bg-[#FF007F]/25"
                : "bg-[#D4FF00]/15 text-[#D4FF00] border-[#D4FF00]/40 hover:bg-[#D4FF00]/25"
            }`}>
            {status === "approved"
              ? <><Pause size={10} className="inline mr-1"/>Deactivate</>
              : <><Check size={10} className="inline mr-1"/>Activate</>}
          </button>
          {dirty && (
            <button onClick={save} className="btn-primary text-xs px-3 py-1.5" data-testid={`admin-locks-save-${seller.user_id}`}>
              <Save size={12} className="inline mr-1" /> Save locks
            </button>
          )}
        </div>
      </div>

      {/* Detail panel */}
      {showDetails && (
        <div className="bg-[#0F0F11] border border-[#3D2845] rounded-xl p-4 mb-3"
          data-testid={`admin-seller-details-${seller.user_id}`}>
          {!details
            ? <div className="text-sm text-[#A1A1AA]">{busy ? "Loading…" : "—"}</div>
            : (
              <div className="grid sm:grid-cols-2 gap-4 text-xs">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#A1A1AA] font-bold mb-2">Profile</div>
                  <DetailRow label="Phone"   value={details.user.phone}/>
                  <DetailRow label="Address" value={details.user.address}/>
                  <DetailRow label="Bio"     value={details.user.bio}/>
                  <DetailRow label="Joined"  value={details.user.created_at?.slice(0,10)}/>
                  <DetailRow label="Approved" value={details.user.approved_at?.slice(0,10) || "—"}/>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#A1A1AA] font-bold mb-2">Performance</div>
                  <DetailRow label="Stores"        value={details.metrics.stores}/>
                  <DetailRow label="Products"      value={details.metrics.products}/>
                  <DetailRow label="Orders"        value={details.metrics.orders}/>
                  <DetailRow label="Units sold"    value={details.metrics.units_sold}/>
                  <DetailRow label="Revenue"       value={`$${details.metrics.revenue}`}/>
                  <DetailRow label="Subscription"
                    value={`${details.subscription.subscription_active ? "Active" : "Inactive"} · expires ${
                      (details.subscription.subscription_expires_at || "—").slice(0,10)
                    }`}/>
                  <DetailRow label="Payments"      value={`${details.payments.length} on file`}/>
                </div>
                {details.payments.length > 0 && (
                  <div className="sm:col-span-2">
                    <div className="text-[10px] uppercase tracking-widest text-[#A1A1AA] font-bold mb-2">Payment history</div>
                    <div className="space-y-1">
                      {details.payments.slice(0, 5).map(p => (
                        <div key={p.payment_id} className="flex items-center justify-between text-xs bg-[#261534] rounded-lg px-3 py-2">
                          <div>
                            <span className="font-bold text-[#D4FF00]">${p.amount}</span>
                            <span className="text-[#A1A1AA]"> · {p.months}mo · {p.method}</span>
                          </div>
                          <div className="text-[10px] text-[#A1A1AA]">{p.created_at?.slice(0,10)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
        </div>
      )}

      <div className="text-[10px] uppercase tracking-widest text-[#A1A1AA] font-bold mb-2">Field locks</div>
      <div className="flex flex-wrap gap-2">
        {lockable.map(f => {
          const isLocked = selected.has(f);
          return (
            <button key={f} onClick={() => toggle(f)}
              data-testid={`admin-lock-${seller.user_id}-${f}`}
              className={`text-xs px-3 py-1.5 rounded-full border flex items-center gap-1 transition ${
                isLocked
                  ? "bg-[#FF007F]/15 text-[#FF007F] border-[#FF007F]/40"
                  : "bg-[#3D2845] text-[#A1A1AA] border-transparent hover:border-[#3D2845]"
              }`}>
              {isLocked ? <Lock size={10} /> : <Unlock size={10} />}
              {f.replace(/_/g, " ")}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="flex justify-between gap-3 py-1 border-b border-[#3D2845]/40 last:border-0">
      <span className="text-[#A1A1AA]">{label}</span>
      <span className="font-bold text-right truncate max-w-[60%]">{value || "—"}</span>
    </div>
  );
}


const ADMIN_STATUSES = ["confirmed", "processing", "shipped", "out_for_delivery", "delivered", "cancelled", "refunded"];
const ADMIN_STATUS_COLOR = (s) => ({
  confirmed: "#D4FF00", processing: "#D4FF00", shipped: "#00F0FF",
  out_for_delivery: "#00F0FF", delivered: "#D4FF00",
  cancelled: "#FF007F", refunded: "#FF007F", exchanged: "#A1A1AA",
}[s] || "#FAFAFA");

function AdminOrdersTab({ orders, onReload }) {
  const [filter, setFilter] = useState("");
  const [returnFilter, setReturnFilter] = useState("");

  const filtered = orders.filter(o =>
    (!filter || o.status === filter) &&
    (!returnFilter || (o.return_status || "none") === returnFilter)
  );

  const forceStatus = async (orderId, status) => {
    const note = window.prompt(`Set order #${orderId.slice(-6).toUpperCase()} to ${status}. Optional note:`, "");
    if (note === null) return;
    try {
      await api.post(`/admin/orders/${orderId}/status`, { status, note });
      onReload();
    } catch (e) {
      alert(formatApiError(e?.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div className="space-y-3">
      <div className="card p-3 flex flex-wrap items-center gap-2">
        <select className="input-dark sm:w-44" value={filter}
          onChange={e => setFilter(e.target.value)} data-testid="admin-orders-filter">
          <option value="">All statuses</option>
          {ADMIN_STATUSES.map(s => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
        </select>
        <select className="input-dark sm:w-44" value={returnFilter}
          onChange={e => setReturnFilter(e.target.value)} data-testid="admin-orders-return-filter">
          <option value="">All returns</option>
          <option value="none">None</option>
          <option value="requested">Requested</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="picked_up">Picked up</option>
          <option value="refunded">Refunded</option>
        </select>
        <div className="ml-auto text-xs text-[#A1A1AA]">{filtered.length} of {orders.length}</div>
      </div>

      {filtered.length === 0 && (
        <div className="card p-8 text-center text-[#A1A1AA]">No orders match.</div>
      )}

      {filtered.map(o => (
        <div key={o.order_id} className="card p-4" data-testid={`admin-order-${o.order_id}`}>
          <div className="flex flex-wrap items-start gap-3 mb-3">
            <div className="flex-1 min-w-[160px]">
              <div className="font-bold text-sm">#{o.order_id.slice(-8).toUpperCase()}</div>
              <div className="text-xs text-[#A1A1AA]">{o.items.length} items · {new Date(o.created_at).toLocaleString()}</div>
            </div>
            <div className="font-display font-bold text-[#D4FF00]">${o.total.toFixed(2)}</div>
            <span className="text-[10px] uppercase font-bold tracking-widest px-2 py-1 rounded"
              style={{ color: ADMIN_STATUS_COLOR(o.status), background: `${ADMIN_STATUS_COLOR(o.status)}1F` }}>
              {o.status.replace("_", " ")}
            </span>
            {o.return_status && o.return_status !== "none" && (
              <span className="text-[10px] uppercase font-bold tracking-widest px-2 py-1 rounded bg-[#00F0FF]/15 text-[#00F0FF]">
                <Undo2 size={10} className="inline mr-1" /> {o.return_status.replace("_", " ")}
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] uppercase tracking-widest text-[#A1A1AA] font-bold">Force status:</span>
            {ADMIN_STATUSES.map(s => (
              <button key={s} onClick={() => forceStatus(o.order_id, s)}
                disabled={o.status === s}
                data-testid={`admin-force-${o.order_id}-${s}`}
                className={`text-[10px] px-2 py-1 rounded-full border transition ${
                  o.status === s
                    ? "border-[#3D2845] bg-[#3D2845] text-[#A1A1AA] cursor-not-allowed"
                    : "border-[#3D2845] hover:border-[#FF007F] hover:text-[#FF007F]"
                }`}>
                <Truck size={10} className="inline mr-1" />{s.replace("_", " ")}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// Approvals — review pending sellers
// ─────────────────────────────────────────────────────────────
function AdminApprovalsTab({ flash }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [details, setDetails] = useState({});
  const load = async () => { try {
    const { data } = await api.get("/admin/sellers/pending");
    setRows(data);
  } catch {} };
  useEffect(() => { load(); }, []);

  const decide = async (uid, decision, note = "") => {
    setBusy(uid);
    try {
      await api.post(`/admin/sellers/${uid}/approval`, { decision, note });
      flash(`Seller ${decision}d`);
      load();
    } catch (e) { alert(formatApiError(e?.response?.data?.detail) || "Failed"); }
    finally { setBusy(null); }
  };

  const toggle = async (uid) => {
    if (openId === uid) { setOpenId(null); return; }
    setOpenId(uid);
    if (!details[uid]) {
      try {
        const [{ data: full }, { data: kyc }] = await Promise.all([
          api.get(`/admin/sellers/${uid}/details`),
          api.get(`/admin/kyc?status=`).then(r => ({ data: (r.data || []).find(k => k.seller_id === uid) || null }))
            .catch(() => ({ data: null })),
        ]);
        setDetails(d => ({ ...d, [uid]: { ...full, kyc } }));
      } catch (e) {
        setDetails(d => ({ ...d, [uid]: { error: "Failed to load profile" } }));
      }
    }
  };

  if (!rows.length) {
    return <div className="card p-10 text-center" data-testid="admin-approvals-empty">
      <UserCheck size={36} className="mx-auto text-[#52525B] mb-3"/>
      <div className="font-display text-xl mb-1">No pending sellers</div>
      <div className="text-sm text-[#A1A1AA]">New seller applications will appear here.</div>
    </div>;
  }

  return (
    <div className="grid gap-3" data-testid="admin-approvals-list">
      {rows.map(u => {
        const d = details[u.user_id];
        const isOpen = openId === u.user_id;
        return (
          <div key={u.user_id} className="card overflow-hidden">
            <div className="p-4 flex flex-col sm:flex-row sm:items-center gap-4">
              {u.picture
                ? <img src={u.picture} className="w-12 h-12 rounded-full" alt=""/>
                : <div className="w-12 h-12 rounded-full bg-[#FF007F] flex items-center justify-center font-bold text-sm">{(u.name||"?").slice(0,2).toUpperCase()}</div>}
              <div className="flex-1">
                <div className="font-bold">{u.name}</div>
                <div className="text-xs text-[#A1A1AA]">{u.email}</div>
                <div className="text-[10px] text-[#52525B] mt-1">Applied {new Date(u.created_at).toLocaleDateString()}</div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => toggle(u.user_id)}
                  data-testid={`admin-view-profile-${u.user_id}`}
                  className="btn-secondary text-xs px-3 py-2">{isOpen ? "Hide profile" : "View profile"}</button>
                <button onClick={() => decide(u.user_id, "approve")}
                  disabled={busy === u.user_id}
                  data-testid={`admin-approve-${u.user_id}`}
                  className="btn-primary text-xs px-3 py-2"><Check size={12} className="inline mr-1"/>Approve</button>
                <button onClick={() => {
                  const note = prompt("Reason for rejection (shared with seller):", "");
                  if (note !== null) decide(u.user_id, "reject", note);
                }}
                  disabled={busy === u.user_id}
                  data-testid={`admin-reject-${u.user_id}`}
                  className="btn-secondary text-xs px-3 py-2 text-[#FF007F]"><X size={12} className="inline mr-1"/>Reject</button>
              </div>
            </div>
            {isOpen && (
              <div className="border-t border-[#3D2845] bg-[#1A0F1F] p-4 grid md:grid-cols-2 gap-4 text-sm" data-testid={`admin-profile-panel-${u.user_id}`}>
                {!d && <div className="text-[#A1A1AA] col-span-full">Loading profile…</div>}
                {d?.error && <div className="text-[#FF007F] col-span-full">{d.error}</div>}
                {d && !d.error && (
                  <>
                    <div className="space-y-1">
                      <div className="font-display font-black uppercase text-xs text-[#D4FF00]">Profile</div>
                      <ProfileRow label="Phone" value={d.user?.phone}/>
                      <ProfileRow label="Address" value={d.user?.address}/>
                      <ProfileRow label="Bio" value={d.user?.bio}/>
                      <ProfileRow label="KYC status" value={
                        <span className={`chip text-[10px] ${d.user?.kyc_status === "approved" ? "active" : ""}`}>
                          {d.user?.kyc_status || "not_submitted"}
                        </span>
                      }/>
                    </div>
                    <div className="space-y-1">
                      <div className="font-display font-black uppercase text-xs text-[#00F0FF]">Stores ({d.stores?.length || 0})</div>
                      {(d.stores || []).map(s => (
                        <div key={s.store_id} className="flex items-center gap-2">
                          {s.logo_url
                            ? <img src={s.logo_url} className="w-8 h-8 rounded object-cover" alt=""/>
                            : <div className="w-8 h-8 rounded bg-[#3D2845]"/>}
                          <div className="text-xs flex-1">
                            <div className="font-bold">{s.name}</div>
                            <div className="text-[#A1A1AA]">{s.followers || 0} followers</div>
                          </div>
                        </div>
                      ))}
                      {!d.stores?.length && <div className="text-xs text-[#A1A1AA]">No stores yet.</div>}
                    </div>
                    {d.kyc && (
                      <div className="md:col-span-2 grid sm:grid-cols-2 gap-3 pt-3 border-t border-[#3D2845]">
                        <div className="font-display font-black uppercase text-xs text-[#FF007F] sm:col-span-2">KYC details ({d.kyc.status})</div>
                        <ProfileRow label="Business" value={`${d.kyc.business_name} (${d.kyc.business_type})`}/>
                        <ProfileRow label="Tax ID" value={d.kyc.tax_id}/>
                        <ProfileRow label="Address" value={[d.kyc.address_line1, d.kyc.city, d.kyc.region, d.kyc.postal_code, d.kyc.country].filter(Boolean).join(", ")}/>
                        <ProfileRow label="ID type" value={d.kyc.id_document_type}/>
                        <ProfileRow label="Bank" value={`${d.kyc.bank_account_holder || "—"} • ••${d.kyc.bank_account_last4 || ""}`}/>
                        {d.kyc.id_document_url && <a href={d.kyc.id_document_url} target="_blank" rel="noreferrer"
                          className="text-xs text-[#00F0FF] underline">View ID document →</a>}
                      </div>
                    )}
                    {!d.kyc && (
                      <div className="md:col-span-2 text-xs text-[#A1A1AA] pt-3 border-t border-[#3D2845]">
                        Seller hasn't submitted KYC yet.
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ProfileRow({ label, value }) {
  return (
    <div className="flex gap-2">
      <span className="text-[10px] uppercase tracking-widest text-[#52525B] font-bold w-24 shrink-0 pt-0.5">{label}</span>
      <span className="text-xs flex-1 break-words">{value || <span className="text-[#52525B]">—</span>}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Subscriptions — per seller billing state + record payment
// ─────────────────────────────────────────────────────────────
function AdminSubscriptionsTab({ flash }) {
  const [rows, setRows] = useState([]);
  const [paying, setPaying] = useState(null);
  const [modifying, setModifying] = useState(null);
  const load = async () => { try {
    const { data } = await api.get("/admin/subscriptions");
    setRows(data);
  } catch {} };
  useEffect(() => { load(); }, []);

  const recordPayment = async (uid, amount, months, note) => {
    try {
      await api.post("/admin/subscriptions/record-payment",
        { seller_id: uid, amount: Number(amount), months: Number(months), note });
      flash("Payment recorded · subscription extended · seller reactivated");
      setPaying(null); load();
    } catch (e) { alert(formatApiError(e?.response?.data?.detail) || "Failed"); }
  };

  const modifySub = async (uid, payload) => {
    try {
      await api.put(`/admin/subscriptions/${uid}`, payload);
      flash("Subscription updated");
      setModifying(null); load();
    } catch (e) { alert(formatApiError(e?.response?.data?.detail) || "Failed"); }
  };

  const expire = async (uid) => {
    if (!window.confirm("Force expire this seller's subscription?")) return;
    try {
      await api.post(`/admin/subscriptions/${uid}/expire`);
      flash("Subscription expired");
      load();
    } catch (e) { alert(formatApiError(e?.response?.data?.detail) || "Failed"); }
  };

  return (
    <div className="card overflow-x-auto" data-testid="admin-subs-table">
      <table className="w-full text-sm">
        <thead className="bg-[#3D2845] text-left">
          <tr>
            <th className="p-3">Seller</th>
            <th className="p-3">Status</th>
            <th className="p-3">Subscription</th>
            <th className="p-3">Last payment</th>
            <th className="p-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => {
            const live = r.subscription_active;
            const exp = r.subscription_expires_at
              ? new Date(r.subscription_expires_at).toLocaleDateString()
              : "—";
            return (
              <tr key={r.user_id} className="border-t border-[#3D2845]"
                data-testid={`admin-sub-row-${r.user_id}`}>
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    {r.picture && <img src={r.picture} className="w-8 h-8 rounded-full" alt=""/>}
                    <div>
                      <div className="font-bold">{r.name}</div>
                      <div className="text-[10px] text-[#A1A1AA]">{r.email}</div>
                    </div>
                  </div>
                </td>
                <td className="p-3">
                  <span className={`chip text-[10px] ${r.seller_status === "approved" ? "active" : ""}`}>
                    {r.seller_status}
                  </span>
                </td>
                <td className="p-3">
                  <div className={`text-xs font-bold ${live ? "text-[#D4FF00]" : "text-[#FF007F]"}`}>
                    {live ? "Active" : "Inactive"}
                  </div>
                  <div className="text-[10px] text-[#A1A1AA]">Expires {exp}</div>
                </td>
                <td className="p-3 text-xs text-[#A1A1AA]">
                  {r.last_payment
                    ? `$${r.last_payment.amount} · ${r.last_payment.months}mo · ${new Date(r.last_payment.created_at).toLocaleDateString()}`
                    : "—"}
                </td>
                <td className="p-3 text-right whitespace-nowrap">
                  <button onClick={() => setPaying(r.user_id)}
                    data-testid={`admin-record-pay-${r.user_id}`}
                    title="Record fee — auto-reactivates seller and extends expiry"
                    className="btn-primary text-xs px-3 py-1.5"><DollarSign size={12} className="inline mr-1"/>Record fee</button>
                  <button onClick={() => setModifying(r)}
                    data-testid={`admin-modify-sub-${r.user_id}`}
                    title="Modify subscription manually"
                    className="ml-1 btn-secondary text-xs px-3 py-1.5"><Pencil size={12} className="inline mr-1"/>Modify</button>
                  {live ? (
                    <button onClick={() => expire(r.user_id)}
                      data-testid={`admin-expire-${r.user_id}`}
                      className="ml-1 btn-secondary text-xs px-3 py-1.5">
                      <Pause size={12} className="inline mr-1"/>Expire</button>
                  ) : (
                    <button onClick={() => modifySub(r.user_id, { active: true })}
                      data-testid={`admin-quick-activate-${r.user_id}`}
                      title="Reactivate without payment (uses existing expiry)"
                      className="ml-1 btn-secondary text-xs px-3 py-1.5 text-[#D4FF00]">
                      <Check size={12} className="inline mr-1"/>Activate</button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="px-3 py-3 text-[10px] text-[#52525B] border-t border-[#3D2845]">
        💡 <span className="font-bold">Record fee</span> = log a payment + extend expiry by N months + auto-reactivate.
        <span className="font-bold ml-3">Modify</span> = edit expiry / active flag without recording money.
        <span className="font-bold ml-3">Activate</span> = re-enable seller using their existing (paid-for) expiry.
      </div>
      {paying && <PaymentModal onClose={() => setPaying(null)}
        onSubmit={({ amount, months, note }) => recordPayment(paying, amount, months, note)}/>}
      {modifying && <ModifySubscriptionModal row={modifying}
        onClose={() => setModifying(null)}
        onSubmit={(payload) => modifySub(modifying.user_id, payload)}/>}
    </div>
  );
}

function ModifySubscriptionModal({ row, onClose, onSubmit }) {
  const [active, setActive] = useState(row.subscription_active);
  const [expires, setExpires] = useState(
    row.subscription_expires_at ? row.subscription_expires_at.slice(0, 16) : ""
  );
  const [note, setNote] = useState("");
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={onClose} data-testid="modify-sub-modal">
      <div onClick={(e) => e.stopPropagation()} className="card w-full max-w-md p-5">
        <div className="font-display font-black text-xl uppercase mb-1">Modify subscription</div>
        <div className="text-xs text-[#A1A1AA] mb-4">
          For <span className="font-bold text-[#FAFAFA]">{row.name}</span> · {row.email}
        </div>
        <label className="flex items-center gap-2 text-sm mb-3">
          <input type="checkbox" checked={active}
            onChange={(e) => setActive(e.target.checked)}
            data-testid="modify-sub-active"/>
          Subscription active
        </label>
        <div className="mb-3">
          <label className="text-[10px] uppercase tracking-widest text-[#A1A1AA] font-bold block mb-1">Expires at</label>
          <input type="datetime-local" value={expires}
            onChange={(e) => setExpires(e.target.value)}
            data-testid="modify-sub-expires"
            className="input-dark w-full px-3 py-2 text-sm"/>
        </div>
        <div className="mb-4">
          <label className="text-[10px] uppercase tracking-widest text-[#A1A1AA] font-bold block mb-1">Note (optional)</label>
          <input value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="Visible to seller in notification"
            className="input-dark w-full px-3 py-2 text-sm"/>
        </div>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="btn-secondary text-xs">Cancel</button>
          <button onClick={() => {
              const payload = { active, note };
              if (expires) payload.expires_at = new Date(expires).toISOString();
              onSubmit(payload);
            }}
            data-testid="modify-sub-confirm"
            className="btn-primary text-xs">Save changes</button>
        </div>
      </div>
    </div>
  );
}

function PaymentModal({ onClose, onSubmit }) {
  const [amount, setAmount] = useState("49");
  const [months, setMonths] = useState("1");
  const [note, setNote]     = useState("");
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={onClose} data-testid="payment-modal">
      <div onClick={(e) => e.stopPropagation()} className="card w-full max-w-md p-5">
        <div className="font-display font-black text-xl uppercase mb-3">Record payment</div>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A1A1AA] font-bold block mb-1">Amount ($)</label>
            <input type="number" step="0.01" value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="payment-amount"
              className="input-dark w-full px-3 py-2 text-sm"/>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A1A1AA] font-bold block mb-1">Months</label>
            <input type="number" min="1" max="24" value={months}
              onChange={(e) => setMonths(e.target.value)}
              data-testid="payment-months"
              className="input-dark w-full px-3 py-2 text-sm"/>
          </div>
        </div>
        <div className="mb-4">
          <label className="text-[10px] uppercase tracking-widest text-[#A1A1AA] font-bold block mb-1">Note (optional)</label>
          <input value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Launch special"
            className="input-dark w-full px-3 py-2 text-sm"/>
        </div>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="btn-secondary text-xs">Cancel</button>
          <button onClick={() => onSubmit({ amount, months, note })}
            data-testid="payment-confirm"
            className="btn-primary text-xs">Record payment</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Announcements
// ─────────────────────────────────────────────────────────────
function AdminAnnouncementsTab({ flash }) {
  const empty = { title: "", body: "", link_url: "", target_role: "all",
                  severity: "info", start_at: "", end_at: "", active: true,
                  target_countries: [] };
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [countryDraft, setCountryDraft] = useState("");

  const load = async () => { try {
    const { data } = await api.get("/admin/announcements");
    setRows(data);
  } catch {} };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { ...form };
      if (payload.start_at === "") payload.start_at = null;
      if (payload.end_at === "")   payload.end_at = null;
      payload.target_countries = (payload.target_countries || [])
        .map(c => (c || "").toUpperCase().trim()).filter(Boolean);
      if (editing) await api.put(`/admin/announcements/${editing}`, payload);
      else         await api.post(`/admin/announcements`, payload);
      flash(editing ? "Announcement updated" : "Announcement created");
      setForm(empty); setEditing(null); setCountryDraft(""); load();
    } catch (e) { alert(formatApiError(e?.response?.data?.detail) || "Failed"); }
  };

  const edit = (r) => {
    setEditing(r.ann_id);
    setForm({ title: r.title, body: r.body, link_url: r.link_url || "",
              target_role: r.target_role || "all", severity: r.severity || "info",
              start_at: r.start_at || "", end_at: r.end_at || "",
              active: r.active !== false,
              target_countries: r.target_countries || [] });
  };
  const remove = async (id) => {
    if (!window.confirm("Delete announcement?")) return;
    try { await api.delete(`/admin/announcements/${id}`); flash("Deleted"); load(); }
    catch {}
  };

  const addCountries = () => {
    const codes = countryDraft.split(/[,\s]+/).map(c => c.toUpperCase().trim()).filter(c => c.length === 2);
    if (!codes.length) return;
    setForm(f => ({ ...f, target_countries: Array.from(new Set([...(f.target_countries||[]), ...codes])) }));
    setCountryDraft("");
  };
  const removeCountry = (cc) => setForm(f => ({ ...f, target_countries: (f.target_countries||[]).filter(c => c !== cc) }));

  return (
    <div className="grid lg:grid-cols-[1fr_380px] gap-4">
      <div className="card overflow-x-auto" data-testid="admin-ann-list">
        <table className="w-full text-sm">
          <thead className="bg-[#3D2845] text-left">
            <tr>
              <th className="p-3">Title</th>
              <th className="p-3">Target</th>
              <th className="p-3">Status</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.ann_id} className="border-t border-[#3D2845]">
                <td className="p-3">
                  <div className="font-bold">{r.title}</div>
                  <div className="text-[10px] text-[#A1A1AA] truncate max-w-xs">{r.body}</div>
                </td>
                <td className="p-3">
                  <span className="chip text-[10px]">{r.target_role}</span>
                  {(r.target_countries || []).length > 0 && (
                    <span className="ml-1 chip text-[10px] bg-[#00F0FF]/10 text-[#00F0FF]">
                      🌍 {r.target_countries.slice(0, 3).join(", ")}{r.target_countries.length > 3 ? ` +${r.target_countries.length - 3}` : ""}
                    </span>
                  )}
                </td>
                <td className="p-3">
                  <span className={`chip text-[10px] ${r.live_status === "live" ? "active" : ""}`}>{r.live_status}</span>
                </td>
                <td className="p-3 text-right whitespace-nowrap">
                  <button onClick={() => edit(r)} className="btn-secondary text-xs px-2 py-1">
                    <Pencil size={11}/></button>
                  <button onClick={() => remove(r.ann_id)} className="ml-1 btn-secondary text-xs px-2 py-1 text-[#FF007F]">
                    <Trash2 size={11}/></button>
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr><td colSpan={4} className="p-6 text-center text-[#A1A1AA] text-sm">No announcements yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="card p-4 h-fit" data-testid="admin-ann-form">
        <div className="font-display font-black text-lg uppercase mb-3">
          {editing ? "Edit announcement" : "New announcement"}
        </div>
        <div className="space-y-3">
          <LabeledInput label="Title" value={form.title} onChange={(v) => setForm(f => ({...f, title: v}))}/>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A1A1AA] font-bold block mb-1">Body</label>
            <textarea value={form.body} rows={3}
              onChange={(e) => setForm(f => ({...f, body: e.target.value}))}
              className="input-dark w-full px-3 py-2 text-sm resize-y"/>
          </div>
          <LabeledInput label="Link URL (optional)" value={form.link_url} onChange={(v) => setForm(f => ({...f, link_url: v}))}/>
          <div className="grid grid-cols-2 gap-2">
            <LabeledSelect label="Target" value={form.target_role} onChange={(v) => setForm(f => ({...f, target_role: v}))}
              options={[["all","All"],["buyer","Buyers"],["seller","Sellers"]]}/>
            <LabeledSelect label="Severity" value={form.severity} onChange={(v) => setForm(f => ({...f, severity: v}))}
              options={[["info","Info"],["success","Success"],["warning","Warning"],["promo","Promo"]]}/>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <LabeledInput label="Start" type="datetime-local" value={form.start_at?.slice(0,16)}
              onChange={(v) => setForm(f => ({...f, start_at: v ? new Date(v).toISOString() : ""}))}/>
            <LabeledInput label="End" type="datetime-local" value={form.end_at?.slice(0,16)}
              onChange={(v) => setForm(f => ({...f, end_at: v ? new Date(v).toISOString() : ""}))}/>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A1A1AA] font-bold block mb-1">
              Countries (ISO 2-letter, blank = worldwide)
            </label>
            <div className="flex gap-2">
              <input value={countryDraft}
                onChange={(e) => setCountryDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCountries(); } }}
                placeholder="e.g. US, IN, GB"
                data-testid="admin-ann-country-input"
                className="input-dark flex-1 px-3 py-2 text-sm"/>
              <button type="button" onClick={addCountries} data-testid="admin-ann-country-add"
                className="btn-secondary text-xs px-3">Add</button>
            </div>
            {(form.target_countries || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="admin-ann-country-chips">
                {form.target_countries.map(cc => (
                  <span key={cc} className="chip text-[10px] flex items-center gap-1">
                    {cc}
                    <button type="button" onClick={() => removeCountry(cc)} className="text-[#FF007F]">×</button>
                  </span>
                ))}
              </div>
            )}
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.active}
              onChange={(e) => setForm(f => ({...f, active: e.target.checked}))}/>
            Active
          </label>
          <div className="flex gap-2 justify-end pt-2">
            {editing && <button onClick={() => { setEditing(null); setForm(empty); }}
              className="btn-secondary text-xs">Cancel</button>}
            <button onClick={save} data-testid="admin-ann-save"
              className="btn-primary text-xs">{editing ? "Update" : "Create"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Ads
// ─────────────────────────────────────────────────────────────
function AdminAdsTab({ flash }) {
  const empty = { title: "", kind: "banner", placement: "home_top",
                  media_url: "", thumbnail_url: "", link_url: "",
                  sponsor_name: "", start_at: "", end_at: "", active: true };
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = async () => { try {
    const { data } = await api.get("/admin/ads");
    setRows(data);
  } catch {} };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { ...form };
      if (payload.start_at === "") payload.start_at = null;
      if (payload.end_at === "")   payload.end_at = null;
      if (editing) await api.put(`/admin/ads/${editing}`, payload);
      else         await api.post(`/admin/ads`, payload);
      flash(editing ? "Ad updated" : "Ad created");
      setForm(empty); setEditing(null); load();
    } catch (e) { alert(formatApiError(e?.response?.data?.detail) || "Failed"); }
  };

  const edit = (r) => {
    setEditing(r.ad_id);
    setForm({ ...r, start_at: r.start_at || "", end_at: r.end_at || "" });
  };
  const remove = async (id) => {
    if (!window.confirm("Delete ad?")) return;
    try { await api.delete(`/admin/ads/${id}`); flash("Deleted"); load(); }
    catch {}
  };

  return (
    <div className="grid lg:grid-cols-[1fr_380px] gap-4">
      <div className="card overflow-x-auto" data-testid="admin-ads-list">
        <div className="px-3 py-2 border-b border-[#3D2845] flex items-center justify-between gap-2">
          <div className="text-xs text-[#A1A1AA]">{rows.length} ad{rows.length === 1 ? "" : "s"} · {rows.filter(r => r.live_status === "live").length} live</div>
          <button onClick={() => setBulkOpen(true)}
            data-testid="admin-ads-bulk-btn"
            className="btn-secondary text-xs px-3 py-1.5">
            <Plus size={12} className="inline mr-1"/>Bulk upload
          </button>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-[#3D2845] text-left">
            <tr>
              <th className="p-3">Title</th>
              <th className="p-3">Kind</th>
              <th className="p-3">Placement</th>
              <th className="p-3">Schedule</th>
              <th className="p-3">Status</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const fmt = (s) => s ? new Date(s).toLocaleDateString() + " " + new Date(s).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}) : "—";
              return (
                <tr key={r.ad_id} className="border-t border-[#3D2845]">
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      {r.media_url && r.kind === "banner" &&
                        <img src={r.media_url} className="w-10 h-10 rounded-lg object-cover" alt=""/>}
                      {r.kind === "video" && <PlayCircle size={24} className="text-[#FF007F]"/>}
                      <div>
                        <div className="font-bold">{r.title}</div>
                        <div className="text-[10px] text-[#A1A1AA]">{r.sponsor_name}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3"><span className="chip text-[10px]">{r.kind}</span></td>
                  <td className="p-3 text-xs">{r.placement}</td>
                  <td className="p-3 text-[10px] text-[#A1A1AA]">
                    <div>From: {fmt(r.start_at)}</div>
                    <div>To: {fmt(r.end_at)}</div>
                  </td>
                  <td className="p-3">
                    <span className={`chip text-[10px] ${r.live_status === "live" ? "active" : ""}`}>{r.live_status}</span>
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <button onClick={() => edit(r)} className="btn-secondary text-xs px-2 py-1"><Pencil size={11}/></button>
                    <button onClick={() => remove(r.ad_id)} className="ml-1 btn-secondary text-xs px-2 py-1 text-[#FF007F]"><Trash2 size={11}/></button>
                  </td>
                </tr>
              );
            })}
            {!rows.length && <tr><td colSpan={6} className="p-6 text-center text-[#A1A1AA] text-sm">No ads yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="card p-4 h-fit" data-testid="admin-ads-form">
        <div className="font-display font-black text-lg uppercase mb-3">
          {editing ? "Edit ad" : "New ad"}
        </div>
        <div className="space-y-3">
          <LabeledInput label="Title" value={form.title} onChange={(v) => setForm(f => ({...f, title: v}))}/>
          <LabeledInput label="Sponsor" value={form.sponsor_name} onChange={(v) => setForm(f => ({...f, sponsor_name: v}))}/>
          <div className="grid grid-cols-2 gap-2">
            <LabeledSelect label="Kind" value={form.kind} onChange={(v) => setForm(f => ({...f, kind: v}))}
              options={[["banner","Banner"],["video","Video"]]}/>
            <LabeledSelect label="Placement" value={form.placement} onChange={(v) => setForm(f => ({...f, placement: v}))}
              options={[["home_top","Home top"],["home_mid","Home mid"],["search_top","Search top"],
                        ["feed_interstitial","Feed interstitial"],["category_top","Category top"]]}/>
          </div>
          <LabeledInput label={form.kind === "video" ? "Video URL" : "Image URL"}
            value={form.media_url} onChange={(v) => setForm(f => ({...f, media_url: v}))}/>
          <LabeledInput label="Thumbnail URL" value={form.thumbnail_url}
            onChange={(v) => setForm(f => ({...f, thumbnail_url: v}))}/>
          <LabeledInput label="Click URL" value={form.link_url}
            onChange={(v) => setForm(f => ({...f, link_url: v}))}/>
          <div className="grid grid-cols-2 gap-2">
            <LabeledInput label="Start (date & time)" type="datetime-local" value={form.start_at?.slice(0,16)}
              onChange={(v) => setForm(f => ({...f, start_at: v ? new Date(v).toISOString() : ""}))}/>
            <LabeledInput label="End (date & time)" type="datetime-local" value={form.end_at?.slice(0,16)}
              onChange={(v) => setForm(f => ({...f, end_at: v ? new Date(v).toISOString() : ""}))}/>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.active}
              onChange={(e) => setForm(f => ({...f, active: e.target.checked}))}/>
            Active
          </label>
          <div className="flex gap-2 justify-end pt-2">
            {editing && <button onClick={() => { setEditing(null); setForm(empty); }}
              className="btn-secondary text-xs">Cancel</button>}
            <button onClick={save} data-testid="admin-ads-save"
              className="btn-primary text-xs">{editing ? "Update" : "Create"}</button>
          </div>
        </div>
      </div>

      {bulkOpen && (
        <BulkUploadModal
          {...ADS_BULK}
          open={bulkOpen}
          onClose={() => setBulkOpen(false)}
          onSuccess={(result) => {
            flash(`Created ${result.count} ads · ${result.errors.length} errors`);
            load(); setBulkOpen(false);
          }}/>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Studio assets: backgrounds + music
// ─────────────────────────────────────────────────────────────
function AdminStudioAssetsTab({ flash }) {
  const [section, setSection] = useState("bgs"); // 'bgs' | 'music'
  return (
    <div>
      <div className="flex gap-2 mb-4" data-testid="admin-studio-section-tabs">
        <button onClick={() => setSection("bgs")}
          className={`chip ${section === "bgs" ? "active" : ""}`}
          data-testid="admin-studio-bgs-tab">
          <ImageIcon size={12} className="inline mr-1"/>Backgrounds
        </button>
        <button onClick={() => setSection("music")}
          className={`chip ${section === "music" ? "active" : ""}`}
          data-testid="admin-studio-music-tab">
          <Music size={12} className="inline mr-1"/>Music
        </button>
      </div>
      {section === "bgs"   && <BackgroundsSection flash={flash}/>}
      {section === "music" && <MusicSection flash={flash}/>}
    </div>
  );
}

function BackgroundsSection({ flash }) {
  const empty = { name: "", kind: "gradient", value: "", active: true };
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [bulkOpen, setBulkOpen] = useState(false);
  const load = async () => { try { setRows((await api.get("/admin/studio/backgrounds")).data); } catch {} };
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      if (editing) await api.put(`/admin/studio/backgrounds/${editing}`, form);
      else         await api.post(`/admin/studio/backgrounds`, form);
      flash(editing ? "Updated" : "Created");
      setForm(empty); setEditing(null); load();
    } catch (e) { alert(formatApiError(e?.response?.data?.detail) || "Failed"); }
  };
  const edit = (r) => { setEditing(r.bg_id); setForm({ ...r }); };
  const remove = async (id) => {
    if (!window.confirm("Delete background?")) return;
    try { await api.delete(`/admin/studio/backgrounds/${id}`); flash("Deleted"); load(); } catch {}
  };

  return (
    <div className="grid lg:grid-cols-[1fr_380px] gap-4">
      <div className="space-y-3">
        <div className="flex items-center justify-end">
          <button onClick={() => setBulkOpen(true)}
            data-testid="admin-bgs-bulk-btn"
            className="btn-secondary text-xs px-3 py-1.5">
            <Plus size={12} className="inline mr-1"/>Bulk upload
          </button>
        </div>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3" data-testid="admin-bgs-grid">
        {rows.map(r => (
          <div key={r.bg_id} className="card overflow-hidden">
            <div className="h-28 flex items-center justify-center text-xs text-white/70 font-bold uppercase"
              style={{ background: r.kind === "image" ? `url(${r.value}) center/cover` : r.value }}>
              {r.name}
            </div>
            <div className="p-3 flex items-center justify-between">
              <div>
                <div className="text-xs font-bold">{r.name}</div>
                <div className="text-[10px] text-[#A1A1AA]">{r.kind} · {r.active === false ? "inactive" : "active"}</div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => edit(r)} className="btn-secondary text-xs px-2 py-1"><Pencil size={11}/></button>
                <button onClick={() => remove(r.bg_id)} className="btn-secondary text-xs px-2 py-1 text-[#FF007F]"><Trash2 size={11}/></button>
              </div>
            </div>
          </div>
        ))}
        {!rows.length && <div className="col-span-full card p-8 text-center text-[#A1A1AA] text-sm">No backgrounds yet.</div>}
        </div>
      </div>

      <div className="card p-4 h-fit" data-testid="admin-bgs-form">
        <div className="font-display font-black text-lg uppercase mb-3">
          {editing ? "Edit background" : "New background"}
        </div>
        <div className="space-y-3">
          <LabeledInput label="Name" value={form.name} onChange={(v) => setForm(f => ({...f, name: v}))}/>
          <LabeledSelect label="Kind" value={form.kind} onChange={(v) => setForm(f => ({...f, kind: v}))}
            options={[["gradient","Gradient CSS"],["image","Image URL"],["solid","Solid color"]]}/>
          <LabeledInput label="Value (CSS / URL / #hex)" value={form.value} onChange={(v) => setForm(f => ({...f, value: v}))}/>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.active}
              onChange={(e) => setForm(f => ({...f, active: e.target.checked}))}/>
            Active (shown in Studio)
          </label>
          <div className="flex gap-2 justify-end pt-2">
            {editing && <button onClick={() => { setEditing(null); setForm(empty); }}
              className="btn-secondary text-xs">Cancel</button>}
            <button onClick={save} className="btn-primary text-xs">{editing ? "Update" : "Create"}</button>
          </div>
        </div>
      </div>
      {bulkOpen && (
        <BulkUploadModal {...BGS_BULK} open={bulkOpen}
          onClose={() => setBulkOpen(false)}
          onSuccess={(result) => {
            flash(`Created ${result.count} backgrounds · ${result.errors.length} errors`);
            load(); setBulkOpen(false);
          }}/>
      )}
    </div>
  );
}

function MusicSection({ flash }) {
  const empty = { title: "", artist: "", genre: "", bpm: 0, duration: 30,
                  cover: "", url: "", active: true };
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [bulkOpen, setBulkOpen] = useState(false);
  const load = async () => { try { setRows((await api.get("/admin/studio/music")).data); } catch {} };
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      const payload = { ...form, bpm: Number(form.bpm || 0), duration: Number(form.duration || 30) };
      if (editing) await api.put(`/admin/studio/music/${editing}`, payload);
      else         await api.post(`/admin/studio/music`, payload);
      flash(editing ? "Updated" : "Created");
      setForm(empty); setEditing(null); load();
    } catch (e) { alert(formatApiError(e?.response?.data?.detail) || "Failed"); }
  };

  return (
    <div className="grid lg:grid-cols-[1fr_380px] gap-4">
      <div className="card overflow-x-auto" data-testid="admin-music-list">
        <div className="px-3 py-2 border-b border-[#3D2845] flex items-center justify-end">
          <button onClick={() => setBulkOpen(true)}
            data-testid="admin-music-bulk-btn"
            className="btn-secondary text-xs px-3 py-1.5">
            <Plus size={12} className="inline mr-1"/>Bulk upload
          </button>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-[#3D2845] text-left">
            <tr><th className="p-3">Track</th><th className="p-3">Genre</th><th className="p-3">BPM</th><th className="p-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.track_id} className="border-t border-[#3D2845]">
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    {r.cover && <img src={r.cover} className="w-10 h-10 rounded-lg object-cover" alt=""/>}
                    <div>
                      <div className="font-bold">{r.title}</div>
                      <div className="text-[10px] text-[#A1A1AA]">{r.artist}</div>
                    </div>
                  </div>
                </td>
                <td className="p-3 text-xs">{r.genre}</td>
                <td className="p-3 text-xs">{r.bpm}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  <button onClick={() => { setEditing(r.track_id); setForm({...r}); }}
                    className="btn-secondary text-xs px-2 py-1"><Pencil size={11}/></button>
                  <button onClick={async () => {
                    if (!window.confirm("Delete track?")) return;
                    try { await api.delete(`/admin/studio/music/${r.track_id}`); flash("Deleted"); load(); } catch {}
                  }} className="ml-1 btn-secondary text-xs px-2 py-1 text-[#FF007F]"><Trash2 size={11}/></button>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={4} className="p-6 text-center text-[#A1A1AA] text-sm">No music tracks yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="card p-4 h-fit" data-testid="admin-music-form">
        <div className="font-display font-black text-lg uppercase mb-3">
          {editing ? "Edit track" : "New track"}
        </div>
        <div className="space-y-3">
          <LabeledInput label="Title" value={form.title} onChange={(v) => setForm(f => ({...f, title: v}))}/>
          <LabeledInput label="Artist" value={form.artist} onChange={(v) => setForm(f => ({...f, artist: v}))}/>
          <div className="grid grid-cols-2 gap-2">
            <LabeledInput label="Genre" value={form.genre} onChange={(v) => setForm(f => ({...f, genre: v}))}/>
            <LabeledInput label="BPM" type="number" value={form.bpm} onChange={(v) => setForm(f => ({...f, bpm: v}))}/>
          </div>
          <LabeledInput label="Duration (s)" type="number" value={form.duration} onChange={(v) => setForm(f => ({...f, duration: v}))}/>
          <LabeledInput label="Cover URL" value={form.cover} onChange={(v) => setForm(f => ({...f, cover: v}))}/>
          <LabeledInput label="Audio URL" value={form.url} onChange={(v) => setForm(f => ({...f, url: v}))}/>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.active}
              onChange={(e) => setForm(f => ({...f, active: e.target.checked}))}/>
            Active
          </label>
          <div className="flex gap-2 justify-end pt-2">
            {editing && <button onClick={() => { setEditing(null); setForm(empty); }}
              className="btn-secondary text-xs">Cancel</button>}
            <button onClick={save} className="btn-primary text-xs">{editing ? "Update" : "Create"}</button>
          </div>
        </div>
      </div>
      {bulkOpen && (
        <BulkUploadModal {...MUSIC_BULK} open={bulkOpen}
          onClose={() => setBulkOpen(false)}
          onSuccess={(result) => {
            flash(`Created ${result.count} tracks · ${result.errors.length} errors`);
            load(); setBulkOpen(false);
          }}/>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Subscription plans (per-tier) — admin CRUD + assignment
// ─────────────────────────────────────────────────────────────
function AdminFieldsTab({ flash }) {
  const [all, setAll] = useState({});
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/profile-field-policies");
      setAll(data || {});
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const setState = async (role, field, state) => {
    const current = all[role]?.fields || {};
    const nextFields = { ...current, [field]: state };
    try {
      const { data } = await api.put(`/admin/profile-field-policies/${role}`,
                                      { fields: nextFields });
      setAll({ ...all, [role]: data });
      flash(`${role}.${field} → ${state}`);
    } catch (e) { flash(e?.response?.data?.detail || "Update failed"); }
  };

  const STATES = [
    { id: "hidden",   label: "Hidden",   color: "#FF007F" },
    { id: "optional", label: "Optional", color: "#A1A1AA" },
    { id: "required", label: "Required", color: "#D4FF00" },
  ];

  return (
    <div className="space-y-5" data-testid="admin-fields-tab">
      <div className="card p-4">
        <div className="font-display font-bold uppercase text-sm mb-1">Profile field policy</div>
        <p className="text-[11px] text-[#A1A1AA]">
          For each role, mark every profile field as <span className="text-[#FF007F] font-bold">Hidden</span>, Optional or <span className="text-[#D4FF00] font-bold">Required</span>. Changes are global and apply to every seller / buyer instantly.
        </p>
      </div>
      {Object.entries(all).map(([role, policy]) => (
        <div key={role} className="card p-4 sm:p-5" data-testid={`fields-role-${role}`}>
          <div className="font-display font-bold text-base uppercase tracking-tight mb-3">
            {role}
          </div>
          <div className="grid gap-2">
            {policy.catalog.map((field) => {
              const state = policy.fields[field] || "optional";
              return (
                <div key={field} className="flex items-center justify-between gap-3 p-2 rounded bg-[#261534] border border-[#3D2845]">
                  <div className="text-sm font-bold font-mono-display text-[#FAFAFA] truncate">{field}</div>
                  <div className="flex gap-1">
                    {STATES.map(s => (
                      <button key={s.id} onClick={() => setState(role, field, s.id)}
                        data-testid={`fields-${role}-${field}-${s.id}`}
                        className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest transition ${
                          state === s.id
                            ? "text-black"
                            : "bg-[#3D2845] text-[#A1A1AA] hover:text-white"
                        }`}
                        style={state === s.id ? { background: s.color } : {}}>
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          {policy.updated_at && (
            <div className="text-[10px] text-[#A1A1AA] mt-3">
              Last updated {policy.updated_at.slice(0, 19).replace("T", " ")}
            </div>
          )}
        </div>
      ))}
      {busy && Object.keys(all).length === 0 && (
        <div className="card p-4 text-sm text-[#A1A1AA]">Loading…</div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// Subscription plans (per-tier) — admin CRUD + assignment
// ─────────────────────────────────────────────────────────────
function AdminPaymentsTab({ flash }) {
  const [policies, setPolicies] = useState([]);
  const [audit, setAudit] = useState([]);
  const [busy, setBusy] = useState(false);
  const [users, setUsers] = useState([]);

  const load = async () => {
    setBusy(true);
    try {
      const [p, a, u] = await Promise.all([
        api.get("/admin/payment-policies"),
        api.get("/admin/payment-policies/audit?limit=20"),
        api.get("/admin/users"),
      ]);
      setPolicies(p.data || []);
      setAudit(a.data || []);
      setUsers(u.data || []);
    } catch (e) { /* surfaced via flash on action handlers */ }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const setMaster = async (method, value) => {
    try {
      await api.put(`/admin/payment-policies/${method}`,
                    { master_enabled: value });
      flash(`${method.toUpperCase()} ${value ? "enabled" : "disabled"}.`);
      load();
    } catch (e) { flash(e?.response?.data?.detail || "Failed"); }
  };

  const toggleDeny = async (method, kind, userId, deny) => {
    try {
      await api.post(`/admin/payment-policies/${method}/${kind}-deny`,
                     { user_id: userId, deny });
      load();
    } catch (e) { flash(e?.response?.data?.detail || "Failed"); }
  };

  const sellersById = useMemo(() => {
    const m = new Map();
    users.forEach(u => m.set(u.user_id, u));
    return m;
  }, [users]);

  return (
    <div className="space-y-5" data-testid="admin-payments-tab">
      <div className="card p-4">
        <div className="font-display font-bold uppercase text-sm mb-1">
          Payment method policies
        </div>
        <p className="text-[11px] text-[#A1A1AA]">
          Master switches apply platform-wide. Use deny-lists to selectively block individual sellers or buyers from a given method (e.g. flag a buyer who's been abusing COD).
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {policies.map((pol) => (
          <PolicyCard key={pol.method} policy={pol}
            users={users} sellersById={sellersById}
            setMaster={setMaster} toggleDeny={toggleDeny}/>
        ))}
        {policies.length === 0 && busy && (
          <div className="card p-4 text-[#A1A1AA] text-sm">Loading…</div>
        )}
      </div>

      <div className="card p-4">
        <div className="font-display font-bold uppercase text-sm mb-3">
          Recent changes
        </div>
        {audit.length === 0 ? (
          <div className="text-sm text-[#A1A1AA]">No policy changes yet.</div>
        ) : (
          <div className="grid gap-2 text-sm" data-testid="admin-payments-audit">
            {audit.map((a, i) => (
              <div key={`${a.method}-${a.at || i}-${i}`} className="flex items-center justify-between gap-3 p-2 rounded bg-[#261534] border border-[#3D2845]">
                <div className="flex-1">
                  <span className="text-[10px] uppercase tracking-widest font-bold text-[#D4FF00] mr-2">
                    {a.method}
                  </span>
                  <span className="text-[#FAFAFA]">{a.action}</span>
                  {a.details?.user_id && (
                    <span className="text-[#A1A1AA] ml-2">
                      · {sellersById.get(a.details.user_id)?.email || a.details.user_id}
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-[#A1A1AA] shrink-0">
                  {a.actor_email || "?"} · {(a.at || "").slice(0, 19).replace("T", " ")}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PolicyCard({ policy, users, sellersById, setMaster, toggleDeny }) {
  const [search, setSearch] = useState("");
  const matches = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return [];
    return users.filter(u =>
      (u.email || "").toLowerCase().includes(s) ||
      (u.name || "").toLowerCase().includes(s)
    ).slice(0, 6);
  }, [search, users]);
  const sellerDeny = policy.seller_deny || [];
  const buyerDeny = policy.buyer_deny || [];

  return (
    <div className="card p-4 sm:p-5" data-testid={`policy-card-${policy.method}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="font-display font-bold text-base uppercase tracking-tight">
          {policy.method}
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <span className="text-[10px] uppercase tracking-widest font-bold text-[#A1A1AA]">
            {policy.master_enabled ? "Live" : "Disabled"}
          </span>
          <input type="checkbox" className="sr-only peer"
            checked={!!policy.master_enabled}
            onChange={(e) => setMaster(policy.method, e.target.checked)}
            data-testid={`policy-master-${policy.method}`}/>
          <span className={`relative w-11 h-6 rounded-full transition ${
            policy.master_enabled ? "bg-[#D4FF00]" : "bg-[#3D2845]"
          }`}>
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
              policy.master_enabled ? "translate-x-5" : ""
            }`}/>
          </span>
        </label>
      </div>

      <DenyList label="Sellers blocked" kind="seller"
        method={policy.method} ids={sellerDeny}
        sellersById={sellersById} toggleDeny={toggleDeny}/>
      <DenyList label="Buyers blocked" kind="buyer"
        method={policy.method} ids={buyerDeny}
        sellersById={sellersById} toggleDeny={toggleDeny}/>

      <div className="mt-3 pt-3 border-t border-[#3D2845]">
        <div className="text-[10px] uppercase tracking-widest font-bold text-[#A1A1AA] mb-1">
          Add to deny-list
        </div>
        <input className="input-dark w-full text-sm" placeholder="Search by email…"
          value={search} onChange={(e) => setSearch(e.target.value)}
          data-testid={`policy-search-${policy.method}`}/>
        {matches.length > 0 && (
          <div className="mt-2 grid gap-1">
            {matches.map(u => (
              <div key={u.user_id} className="flex items-center justify-between p-2 rounded bg-[#261534]">
                <div>
                  <div className="text-sm font-bold">{u.name || u.email}</div>
                  <div className="text-[11px] text-[#A1A1AA]">{u.email} · {u.role}</div>
                </div>
                <div className="flex gap-1">
                  {u.role === "seller" && (
                    <button onClick={() => { toggleDeny(policy.method, "seller", u.user_id, true); setSearch(""); }}
                      className="chip text-[10px]"
                      data-testid={`policy-add-seller-${policy.method}-${u.user_id}`}>
                      Block seller
                    </button>
                  )}
                  <button onClick={() => { toggleDeny(policy.method, "buyer", u.user_id, true); setSearch(""); }}
                    className="chip text-[10px]"
                    data-testid={`policy-add-buyer-${policy.method}-${u.user_id}`}>
                    Block buyer
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DenyList({ label, kind, method, ids, sellersById, toggleDeny }) {
  if (!ids.length) return null;
  return (
    <div className="mt-2">
      <div className="text-[10px] uppercase tracking-widest font-bold text-[#FF007F] mb-1">
        {label} ({ids.length})
      </div>
      <div className="flex flex-wrap gap-1">
        {ids.map((id) => {
          const u = sellersById.get(id);
          return (
            <span key={id}
              data-testid={`policy-deny-${method}-${kind}-${id}`}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#FF007F]/10 border border-[#FF007F]/30 text-[11px]">
              {u?.email || id}
              <button onClick={() => toggleDeny(method, kind, id, false)}
                className="ml-1 text-[#FF007F] hover:text-[#FAFAFA]">×</button>
            </span>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Subscription plans (per-tier) — admin CRUD + assignment
// ─────────────────────────────────────────────────────────────
function AdminPlansTab({ flash }) {
  const empty = { name: "", slug: "", price: 0, billing_cycle: "monthly",
                  audience: "seller",
                  features: [], description: "", sort_order: 0, active: true };
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [featureDraft, setFeatureDraft] = useState("");
  const [sellers, setSellers] = useState([]);
  const [assigning, setAssigning] = useState(null);
  const [audienceFilter, setAudienceFilter] = useState("all");

  const load = async () => {
    try {
      const [{ data: plans }, { data: subs }] = await Promise.all([
        api.get("/admin/plans"),
        api.get("/admin/subscriptions").catch(() => ({ data: [] })),
      ]);
      setRows(plans);
      setSellers(subs);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { ...form, price: parseFloat(form.price) || 0,
                        sort_order: parseInt(form.sort_order) || 0 };
      if (editing) await api.put(`/admin/plans/${editing}`, payload);
      else         await api.post(`/admin/plans`, payload);
      flash(editing ? "Plan updated" : "Plan created");
      setForm(empty); setEditing(null); setFeatureDraft(""); load();
    } catch (e) { alert(formatApiError(e?.response?.data?.detail) || "Failed"); }
  };
  const edit = (r) => {
    setEditing(r.plan_id);
    setForm({ name: r.name, slug: r.slug, price: r.price,
              billing_cycle: r.billing_cycle || "monthly",
              audience: r.audience || "seller",
              features: r.features || [],
              description: r.description || "",
              sort_order: r.sort_order || 0,
              active: r.active !== false });
  };
  const remove = async (id) => {
    if (!window.confirm("Delete plan? (will fail if any seller is on it)")) return;
    try { await api.delete(`/admin/plans/${id}`); flash("Deleted"); load(); }
    catch (e) { alert(formatApiError(e?.response?.data?.detail) || "Failed"); }
  };
  const assign = async (sellerId, planId) => {
    if (!planId) return;
    try {
      await api.post(`/admin/sellers/${sellerId}/plan/${planId}`);
      flash("Plan assigned");
      load();
    } catch (e) { alert(formatApiError(e?.response?.data?.detail) || "Failed"); }
    finally { setAssigning(null); }
  };
  const addFeature = () => {
    const f = featureDraft.trim(); if (!f) return;
    setForm(x => ({ ...x, features: [...(x.features || []), f] }));
    setFeatureDraft("");
  };
  const rmFeature = (i) => setForm(x => ({ ...x, features: x.features.filter((_, idx) => idx !== i) }));

  return (
    <div className="grid lg:grid-cols-[1fr_380px] gap-4" data-testid="admin-plans-tab">
      <div className="space-y-4">
        {/* Audience filter chips */}
        <div className="flex gap-2 flex-wrap" data-testid="admin-plans-audience-filter">
          {[["all","All"],["seller","Sellers"],["buyer","Buyers"],["both","Both"]].map(([k, l]) => (
            <button key={k} onClick={() => setAudienceFilter(k)}
              data-testid={`admin-plans-aud-${k}`}
              className={`text-xs px-3 py-1.5 rounded-full font-bold uppercase tracking-widest transition border ${
                audienceFilter === k
                  ? "bg-[#D4FF00] text-black border-[#D4FF00]"
                  : "bg-[#261534] text-[#A1A1AA] border-[#3D2845] hover:text-[#FAFAFA]"
              }`}>{l}</button>
          ))}
        </div>
        <div className="card overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-plans-list">
            <thead className="bg-[#3D2845] text-left">
              <tr>
                <th className="p-3">Plan</th>
                <th className="p-3">Audience</th>
                <th className="p-3">Price</th>
                <th className="p-3">Features</th>
                <th className="p-3">Status</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.filter(r => audienceFilter === "all" || (r.audience || "seller") === audienceFilter).map(r => (
                <tr key={r.plan_id} className="border-t border-[#3D2845]">
                  <td className="p-3">
                    <div className="font-bold">{r.name}</div>
                    <div className="text-[10px] text-[#A1A1AA]">{r.slug} · order {r.sort_order || 0}</div>
                  </td>
                  <td className="p-3">
                    <span className={`text-[10px] uppercase font-bold tracking-widest px-2 py-1 rounded ${
                      (r.audience || "seller") === "buyer" ? "bg-[#FF007F]/15 text-[#FF007F]" :
                      r.audience === "both" ? "bg-[#00F0FF]/15 text-[#00F0FF]" :
                      "bg-[#D4FF00]/15 text-[#D4FF00]"
                    }`}>{r.audience || "seller"}</span>
                  </td>
                  <td className="p-3 font-bold text-[#D4FF00]">${r.price}<span className="text-[10px] text-[#A1A1AA]">/{r.billing_cycle === "yearly" ? "yr" : "mo"}</span></td>
                  <td className="p-3 text-xs">{(r.features || []).slice(0, 2).join(", ")}{(r.features || []).length > 2 && ` +${r.features.length - 2}`}</td>
                  <td className="p-3"><span className={`chip text-[10px] ${r.active === false ? "" : "active"}`}>{r.active === false ? "off" : "on"}</span></td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <button onClick={() => edit(r)} data-testid={`admin-plan-edit-${r.slug}`} className="btn-secondary text-xs px-2 py-1"><Pencil size={11}/></button>
                    <button onClick={() => remove(r.plan_id)} className="ml-1 btn-secondary text-xs px-2 py-1 text-[#FF007F]"><Trash2 size={11}/></button>
                  </td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={6} className="p-6 text-center text-[#A1A1AA] text-sm">No plans yet — create one →</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="card overflow-x-auto" data-testid="admin-plans-assignments">
          <div className="px-4 pt-3 pb-2 font-display font-black uppercase text-sm">Assignments</div>
          <table className="w-full text-sm">
            <thead className="bg-[#3D2845] text-left">
              <tr>
                <th className="p-3">Seller</th>
                <th className="p-3">Current plan</th>
                <th className="p-3 text-right">Assign</th>
              </tr>
            </thead>
            <tbody>
              {sellers.map(s => {
                const cur = rows.find(p => p.plan_id === s.subscription_plan_id);
                return (
                  <tr key={s.user_id} className="border-t border-[#3D2845]">
                    <td className="p-3">
                      <div className="font-bold">{s.name}</div>
                      <div className="text-[10px] text-[#A1A1AA]">{s.email}</div>
                    </td>
                    <td className="p-3">
                      {cur
                        ? <span className="chip text-[10px] active">{cur.name}</span>
                        : <span className="chip text-[10px] text-[#A1A1AA]">Unassigned</span>}
                    </td>
                    <td className="p-3 text-right">
                      <select className="input-dark text-xs px-2 py-1"
                        value={s.subscription_plan_id || ""}
                        onChange={(e) => assign(s.user_id, e.target.value)}
                        data-testid={`admin-plan-assign-${s.user_id}`}>
                        <option value="">— pick plan —</option>
                        {rows.filter(p => p.active !== false).map(p => (
                          <option key={p.plan_id} value={p.plan_id}>{p.name} (${p.price})</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                );
              })}
              {!sellers.length && <tr><td colSpan={3} className="p-6 text-center text-[#A1A1AA] text-sm">No sellers yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card p-4 h-fit" data-testid="admin-plan-form">
        <div className="font-display font-black text-lg uppercase mb-3">{editing ? "Edit plan" : "New plan"}</div>
        <div className="space-y-3">
          <LabeledInput label="Name" value={form.name} onChange={(v) => setForm(f => ({...f, name: v}))}/>
          <LabeledInput label="Slug (lowercase)" value={form.slug}
            onChange={(v) => setForm(f => ({...f, slug: v.toLowerCase().replace(/[^a-z0-9_-]/g, "")}))}/>
          <div className="grid grid-cols-3 gap-2">
            <LabeledInput label="Price" type="number" value={form.price} onChange={(v) => setForm(f => ({...f, price: v}))}/>
            <LabeledSelect label="Cycle" value={form.billing_cycle} onChange={(v) => setForm(f => ({...f, billing_cycle: v}))}
              options={[["monthly","Monthly"],["yearly","Yearly"]]}/>
            <LabeledSelect label="Audience" value={form.audience} onChange={(v) => setForm(f => ({...f, audience: v}))}
              options={[["seller","Sellers"],["buyer","Buyers"],["both","Both"]]}/>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A1A1AA] font-bold block mb-1">Description</label>
            <textarea value={form.description} rows={2}
              onChange={(e) => setForm(f => ({...f, description: e.target.value}))}
              className="input-dark w-full px-3 py-2 text-sm"/>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A1A1AA] font-bold block mb-1">Features</label>
            <div className="flex gap-2">
              <input value={featureDraft} onChange={(e) => setFeatureDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addFeature(); } }}
                placeholder="e.g. Unlimited products"
                className="input-dark flex-1 px-3 py-2 text-sm"/>
              <button type="button" onClick={addFeature} className="btn-secondary text-xs px-3">Add</button>
            </div>
            {(form.features || []).length > 0 && (
              <ul className="mt-2 space-y-1">
                {form.features.map((f, i) => (
                  <li key={`${f}-${i}`} className="flex items-center gap-2 text-xs">
                    <Check size={11} className="text-[#D4FF00]"/>
                    <span className="flex-1">{f}</span>
                    <button onClick={() => rmFeature(i)} className="text-[#FF007F]">×</button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <LabeledInput label="Sort order" type="number" value={form.sort_order}
            onChange={(v) => setForm(f => ({...f, sort_order: v}))}/>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.active}
              onChange={(e) => setForm(f => ({...f, active: e.target.checked}))}/>
            Active (visible to subscribers)
          </label>
          <div className="flex gap-2 justify-end pt-2">
            {editing && <button onClick={() => { setEditing(null); setForm(empty); }} className="btn-secondary text-xs">Cancel</button>}
            <button onClick={save} className="btn-primary text-xs" data-testid="admin-plan-save">{editing ? "Update" : "Create"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Ad A/B testing experiments
// ─────────────────────────────────────────────────────────────
function AdminExperimentsTab({ flash }) {
  const empty = { name: "", placement: "home_top", variant_ad_ids: [],
                  traffic_split: null, start_at: "", end_at: "", active: true };
  const [rows, setRows] = useState([]);
  const [ads, setAds] = useState([]);
  const [form, setForm] = useState(empty);

  const load = async () => {
    try {
      const [{ data: exps }, { data: allAds }] = await Promise.all([
        api.get("/admin/experiments"),
        api.get("/admin/ads"),
      ]);
      setRows(exps);
      setAds(allAds);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (form.variant_ad_ids.length < 2) { alert("Select at least 2 ads as variants."); return; }
    try {
      const payload = { ...form };
      if (payload.start_at === "") payload.start_at = null;
      if (payload.end_at === "")   payload.end_at = null;
      await api.post(`/admin/experiments`, payload);
      flash("Experiment created");
      setForm(empty); load();
    } catch (e) { alert(formatApiError(e?.response?.data?.detail) || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete experiment?")) return;
    try { await api.delete(`/admin/experiments/${id}`); flash("Deleted"); load(); }
    catch {}
  };
  const toggleVariant = (adId) => {
    setForm(f => ({
      ...f,
      variant_ad_ids: f.variant_ad_ids.includes(adId)
        ? f.variant_ad_ids.filter(i => i !== adId)
        : [...f.variant_ad_ids, adId]
    }));
  };

  const placementsFromAds = Array.from(new Set(ads.map(a => a.placement))).sort();
  const filteredAds = ads.filter(a => a.placement === form.placement);

  return (
    <div className="grid lg:grid-cols-[1fr_380px] gap-4" data-testid="admin-experiments-tab">
      <div className="space-y-3" data-testid="admin-experiments-list">
        {rows.map(exp => (
          <div key={exp.experiment_id} className="card p-4">
            <div className="flex items-start justify-between mb-3 gap-3">
              <div>
                <div className="font-display font-black uppercase text-lg">{exp.name}</div>
                <div className="text-xs text-[#A1A1AA]">
                  Placement: <span className="text-[#FAFAFA]">{exp.placement}</span> ·
                  {exp.active === false ? " inactive" : " live"} ·
                  {exp.start_at ? ` from ${new Date(exp.start_at).toLocaleDateString()}` : ""}
                  {exp.end_at ? ` → ${new Date(exp.end_at).toLocaleDateString()}` : ""}
                </div>
              </div>
              <button onClick={() => remove(exp.experiment_id)} className="btn-secondary text-xs text-[#FF007F]"><Trash2 size={11}/></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              {(exp.variants || []).map((v, i) => (
                <div key={v.ad_id} className="rounded-lg border border-[#3D2845] p-3 bg-[#1A0F1F]">
                  <div className="flex items-start gap-2">
                    {v.media_url && v.kind === "banner"
                      ? <img src={v.media_url} className="w-16 h-12 object-cover rounded" alt=""/>
                      : <div className="w-16 h-12 rounded bg-[#3D2845] flex items-center justify-center text-[10px]">{v.kind}</div>}
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm truncate">{v.title}</div>
                      <div className="text-[10px] text-[#A1A1AA]">Variant {String.fromCharCode(65 + i)} · split {(((exp.traffic_split || [])[i] ?? 1/exp.variants.length) * 100).toFixed(0)}%</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-1 mt-2 text-center">
                    <Stat label="Imps" value={v.impressions || 0}/>
                    <Stat label="Clicks" value={v.clicks || 0}/>
                    <Stat label="CTR" value={`${v.ctr || 0}%`} highlight={v.ctr >= 1}/>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
        {!rows.length && (
          <div className="card p-10 text-center text-[#A1A1AA] text-sm">
            No A/B experiments yet. Pick at least 2 ads with the same placement to start a test.
          </div>
        )}
      </div>

      <div className="card p-4 h-fit" data-testid="admin-experiment-form">
        <div className="font-display font-black text-lg uppercase mb-3">New A/B test</div>
        <div className="space-y-3">
          <LabeledInput label="Test name" value={form.name} onChange={(v) => setForm(f => ({...f, name: v}))}/>
          <LabeledSelect label="Placement" value={form.placement}
            onChange={(v) => setForm(f => ({...f, placement: v, variant_ad_ids: []}))}
            options={(placementsFromAds.length ? placementsFromAds : ["home_top","home_mid","search_top","feed_interstitial","category_top"]).map(p => [p, p])}/>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A1A1AA] font-bold block mb-1">
              Variants (pick 2+ ads from this placement)
            </label>
            <div className="space-y-1.5 max-h-56 overflow-y-auto">
              {filteredAds.map(a => (
                <label key={a.ad_id} className="flex items-center gap-2 p-2 rounded border border-[#3D2845] hover:border-[#D4FF00] cursor-pointer text-sm">
                  <input type="checkbox" checked={form.variant_ad_ids.includes(a.ad_id)}
                    onChange={() => toggleVariant(a.ad_id)}
                    data-testid={`admin-exp-var-${a.ad_id}`}/>
                  <span className="flex-1 truncate">{a.title}</span>
                  <span className="text-[10px] text-[#A1A1AA]">{a.kind}</span>
                </label>
              ))}
              {!filteredAds.length && <div className="text-xs text-[#A1A1AA] p-3 text-center">No ads at this placement yet. Create some in the Ads tab first.</div>}
            </div>
            <div className="text-[10px] text-[#52525B] mt-1">Traffic will be split evenly. {form.variant_ad_ids.length} selected.</div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <LabeledInput label="Start" type="datetime-local" value={form.start_at?.slice(0,16)}
              onChange={(v) => setForm(f => ({...f, start_at: v ? new Date(v).toISOString() : ""}))}/>
            <LabeledInput label="End" type="datetime-local" value={form.end_at?.slice(0,16)}
              onChange={(v) => setForm(f => ({...f, end_at: v ? new Date(v).toISOString() : ""}))}/>
          </div>
          <div className="flex justify-end pt-2">
            <button onClick={create} className="btn-primary text-xs" data-testid="admin-exp-create">Launch test</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className={`rounded p-1.5 ${highlight ? "bg-[#D4FF00]/10 text-[#D4FF00]" : "bg-[#3D2845] text-[#FAFAFA]"}`}>
      <div className="text-[9px] uppercase tracking-wider text-[#A1A1AA]">{label}</div>
      <div className="font-bold text-sm">{value}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Sub-admins (super-admin only)
// ─────────────────────────────────────────────────────────────
function AdminSubAdminsTab({ flash, currentUser }) {
  const empty = { email: "", password: "", name: "", permissions: [] };
  const [rows, setRows] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);   // user_id of row being edited

  const load = async () => {
    try {
      const [{ data: list }, { data: meta }] = await Promise.all([
        api.get("/admin/sub-admins"),
        api.get("/admin/permissions"),
      ]);
      setRows(list);
      setCatalog(meta.permissions || []);
    } catch (e) {
      // 403 = not super admin
    }
  };
  useEffect(() => { load(); }, []);

  const groups = useMemo(() => {
    const g = {};
    catalog.forEach(p => {
      g[p.group] = g[p.group] || [];
      g[p.group].push(p);
    });
    return g;
  }, [catalog]);

  const togglePerm = (key) => setForm(f => ({
    ...f,
    permissions: f.permissions.includes(key)
      ? f.permissions.filter(p => p !== key)
      : [...f.permissions, key]
  }));
  const allPermKeys = catalog.map(p => p.key);
  const setAllPerms = () => setForm(f => ({ ...f, permissions: [...allPermKeys] }));
  const clearPerms = () => setForm(f => ({ ...f, permissions: [] }));

  const create = async () => {
    if (!form.email || !form.password || !form.name) {
      alert("Email, name, and password are required."); return;
    }
    if (form.password.length < 6) { alert("Password must be at least 6 characters."); return; }
    try {
      await api.post(`/admin/sub-admins`, form);
      flash("Sub-admin created");
      setForm(empty);
      load();
    } catch (e) { alert(formatApiError(e?.response?.data?.detail) || "Failed"); }
  };

  const startEdit = (r) => {
    setEditing(r.user_id);
    setForm({ email: r.email, password: "", name: r.name,
              permissions: [...(r.permissions || [])] });
  };
  const saveEdit = async () => {
    try {
      const payload = { name: form.name, permissions: form.permissions };
      if (form.password) payload.password = form.password;
      await api.put(`/admin/sub-admins/${editing}`, payload);
      flash("Sub-admin updated");
      setEditing(null); setForm(empty); load();
    } catch (e) { alert(formatApiError(e?.response?.data?.detail) || "Failed"); }
  };
  const cancelEdit = () => { setEditing(null); setForm(empty); };

  const toggleActive = async (r) => {
    try {
      await api.put(`/admin/sub-admins/${r.user_id}`, { active: !r.active });
      flash(r.active ? "Disabled" : "Enabled"); load();
    } catch (e) { alert(formatApiError(e?.response?.data?.detail) || "Failed"); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete sub-admin ${r.email}?`)) return;
    try { await api.delete(`/admin/sub-admins/${r.user_id}`); flash("Deleted"); load(); }
    catch (e) { alert(formatApiError(e?.response?.data?.detail) || "Failed"); }
  };

  const isSuperMe = !!currentUser?.is_super_admin || currentUser?.email === "admin@shoprizz.com";
  if (!isSuperMe) {
    return <div className="card p-10 text-center" data-testid="admin-subadmins-forbidden">
      <Shield size={36} className="mx-auto text-[#FF007F] mb-3"/>
      <div className="font-display text-xl mb-1">Super-admin access required</div>
      <div className="text-sm text-[#A1A1AA]">Only the super-admin can create and manage sub-admins.</div>
    </div>;
  }

  return (
    <div className="grid lg:grid-cols-[1fr_420px] gap-4" data-testid="admin-subadmins-tab">
      <div className="card overflow-x-auto" data-testid="admin-subadmins-list">
        <table className="w-full text-sm">
          <thead className="bg-[#3D2845] text-left">
            <tr>
              <th className="p-3">Admin</th>
              <th className="p-3">Permissions</th>
              <th className="p-3">Status</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.user_id} className="border-t border-[#3D2845]">
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    {r.picture
                      ? <img src={r.picture} className="w-8 h-8 rounded-full" alt=""/>
                      : <div className="w-8 h-8 rounded-full bg-[#3D2845]"/>}
                    <div>
                      <div className="font-bold flex items-center gap-1">
                        {r.name}
                        {r.is_super_admin && (
                          <span title="Super admin" className="ml-1">
                            <ShieldCheck size={12} className="text-[#D4FF00] inline"/>
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-[#A1A1AA]">{r.email}</div>
                    </div>
                  </div>
                </td>
                <td className="p-3">
                  {r.is_super_admin
                    ? <span className="chip text-[10px] active">All permissions</span>
                    : (r.permissions || []).length === 0
                      ? <span className="text-[10px] text-[#FF007F]">No permissions — login allowed but no UI access</span>
                      : <div className="flex flex-wrap gap-1 max-w-xs">
                          {(r.permissions || []).slice(0, 4).map(p => (
                            <span key={p} className="chip text-[9px]">{p}</span>
                          ))}
                          {(r.permissions || []).length > 4 && (
                            <span className="text-[10px] text-[#A1A1AA]">+{r.permissions.length - 4} more</span>
                          )}
                        </div>}
                </td>
                <td className="p-3">
                  <span className={`chip text-[10px] ${r.active === false ? "" : "active"}`}>
                    {r.active === false ? "disabled" : "active"}
                  </span>
                </td>
                <td className="p-3 text-right whitespace-nowrap">
                  {!r.is_super_admin && (
                    <>
                      <button onClick={() => startEdit(r)} data-testid={`subadmin-edit-${r.user_id}`}
                        className="btn-secondary text-xs px-2 py-1"><Pencil size={11}/></button>
                      <button onClick={() => toggleActive(r)}
                        className="ml-1 btn-secondary text-xs px-2 py-1"
                        title={r.active === false ? "Enable login" : "Disable login"}>
                        {r.active === false ? <PlayCircle size={11}/> : <Pause size={11}/>}
                      </button>
                      <button onClick={() => remove(r)} data-testid={`subadmin-del-${r.user_id}`}
                        className="ml-1 btn-secondary text-xs px-2 py-1 text-[#FF007F]"><Trash2 size={11}/></button>
                    </>
                  )}
                  {r.is_super_admin && r.user_id === currentUser.user_id && (
                    <span className="text-[10px] text-[#A1A1AA]">It's you</span>
                  )}
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr><td colSpan={4} className="p-6 text-center text-[#A1A1AA] text-sm">No admins yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="card p-4 h-fit" data-testid="admin-subadmin-form">
        <div className="font-display font-black text-lg uppercase mb-3 flex items-center gap-2">
          <KeyRound size={16} className="text-[#D4FF00]"/>
          {editing ? "Edit sub-admin" : "New sub-admin"}
        </div>
        <div className="space-y-3">
          <LabeledInput label="Full name" value={form.name} onChange={(v) => setForm(f => ({...f, name: v}))}/>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A1A1AA] font-bold block mb-1">
              Email {editing && <span className="text-[#52525B]">(can't change)</span>}
            </label>
            <input type="email" value={form.email} disabled={!!editing}
              onChange={(e) => setForm(f => ({...f, email: e.target.value.toLowerCase()}))}
              data-testid="subadmin-form-email"
              className="input-dark w-full px-3 py-2 text-sm disabled:opacity-50"/>
          </div>
          <LabeledInput label={editing ? "New password (leave blank to keep current)" : "Password (min 6 chars)"}
            type="password" value={form.password}
            onChange={(v) => setForm(f => ({...f, password: v}))}/>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] uppercase tracking-widest text-[#A1A1AA] font-bold">
                Permissions ({form.permissions.length} of {catalog.length})
              </label>
              <div className="flex gap-1">
                <button type="button" onClick={setAllPerms}
                  className="text-[10px] text-[#D4FF00] hover:underline">All</button>
                <button type="button" onClick={clearPerms}
                  className="text-[10px] text-[#A1A1AA] hover:underline ml-2">None</button>
              </div>
            </div>
            <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
              {Object.entries(groups).map(([groupName, perms]) => (
                <div key={groupName}>
                  <div className="text-[9px] uppercase tracking-widest text-[#52525B] font-bold mb-1">{groupName}</div>
                  <div className="space-y-1">
                    {perms.map(p => (
                      <label key={p.key} className="flex items-center gap-2 p-1.5 rounded border border-[#3D2845] hover:border-[#D4FF00] cursor-pointer text-xs">
                        <input type="checkbox" checked={form.permissions.includes(p.key)}
                          onChange={() => togglePerm(p.key)}
                          data-testid={`subadmin-perm-${p.key}`}/>
                        <span className="flex-1">{p.label}</span>
                        <span className="text-[9px] text-[#52525B] font-mono">{p.key}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-2 justify-end pt-2">
            {editing && <button onClick={cancelEdit} className="btn-secondary text-xs">Cancel</button>}
            <button onClick={editing ? saveEdit : create} data-testid="subadmin-save"
              className="btn-primary text-xs">{editing ? "Update" : "Create sub-admin"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Home page customisation (super-admin / home.manage)
// Sub-tabs: Hero · Marquee · Sections · Categories
// ─────────────────────────────────────────────────────────────
function AdminHomeTab({ flash }) {
  const [sub, setSub] = useState("hero");
  const SUB_TABS = [
    ["hero", "Hero / banners"],
    ["marquee", "Marquee"],
    ["sections", "Sections"],
    ["categories", "Categories"],
  ];
  return (
    <div className="space-y-4" data-testid="admin-home-tab">
      <div className="flex gap-1 overflow-x-auto no-scrollbar border-b border-[#3D2845] -mt-4 mb-4">
        {SUB_TABS.map(([k, l]) => (
          <button key={k} onClick={() => setSub(k)} data-testid={`admin-home-sub-${k}`}
            className={`shrink-0 px-3 py-2 text-xs font-bold uppercase tracking-wider border-b-2 transition ${
              sub === k ? "border-[#D4FF00] text-[#D4FF00]" : "border-transparent text-[#A1A1AA] hover:text-[#FAFAFA]"
            }`}>{l}</button>
        ))}
      </div>
      {sub === "hero"       && <HomeHeroPanel flash={flash}/>}
      {sub === "marquee"    && <HomeMarqueePanel flash={flash}/>}
      {sub === "sections"   && <HomeSectionsPanel flash={flash}/>}
      {sub === "categories" && <HomeCategoriesPanel flash={flash}/>}
    </div>
  );
}

function HomeHeroPanel({ flash }) {
  const empty = { title: "", subtitle: "", image: "", cta_label: "Shop Live",
                  cta_href: "/live", order: 0, active: true };
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    try { const { data } = await api.get("/admin/banners"); setRows(data); }
    catch {}
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.title.trim() || !form.image.trim()) {
      alert("Title and image are required."); return;
    }
    try {
      const payload = { ...form, order: parseInt(form.order) || 0 };
      if (editing) await api.put(`/admin/banners/${editing}`, payload);
      else         await api.post(`/admin/banners`, payload);
      flash(editing ? "Banner updated" : "Banner created");
      setForm(empty); setEditing(null); load();
    } catch (e) { alert(formatApiError(e?.response?.data?.detail) || "Failed"); }
  };
  const edit = (r) => {
    setEditing(r.banner_id);
    setForm({ title: r.title, subtitle: r.subtitle || "", image: r.image,
              cta_label: r.cta_label || "Shop Live",
              cta_href: r.cta_href || "/live",
              order: r.order || 0, active: r.active !== false });
  };
  const remove = async (id) => {
    if (!window.confirm("Delete banner?")) return;
    try { await api.delete(`/admin/banners/${id}`); flash("Deleted"); load(); }
    catch {}
  };
  const onPickFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 2_500_000) { alert("Image too large (max 2.5MB)."); return; }
    const reader = new FileReader();
    reader.onload = () => setForm(s => ({ ...s, image: reader.result }));
    reader.readAsDataURL(f);
  };

  return (
    <div className="grid lg:grid-cols-[1fr_400px] gap-4">
      <div className="space-y-3" data-testid="home-hero-list">
        {rows.map(r => (
          <div key={r.banner_id} className="card overflow-hidden flex flex-col sm:flex-row">
            <img src={r.image} alt={r.title} className="w-full sm:w-44 h-32 object-cover"/>
            <div className="p-3 flex-1">
              <div className="font-display font-black uppercase text-base">{r.title}</div>
              <div className="text-xs text-[#A1A1AA] mt-1">{r.subtitle}</div>
              <div className="text-[10px] text-[#52525B] mt-2 font-mono-display">
                CTA: {r.cta_label} → {r.cta_href} · order {r.order || 0}
              </div>
              <div className="flex gap-2 mt-3">
                <span className={`chip text-[10px] ${r.active === false ? "" : "active"}`}>
                  {r.active === false ? "off" : "on"}
                </span>
                <div className="ml-auto flex gap-1">
                  <button onClick={() => edit(r)} data-testid={`hero-edit-${r.banner_id}`}
                    className="btn-secondary text-xs px-2 py-1"><Pencil size={11}/></button>
                  <button onClick={() => remove(r.banner_id)} data-testid={`hero-del-${r.banner_id}`}
                    className="btn-secondary text-xs px-2 py-1 text-[#FF007F]"><Trash2 size={11}/></button>
                </div>
              </div>
            </div>
          </div>
        ))}
        {!rows.length && <div className="card p-8 text-center text-sm text-[#A1A1AA]">No banners yet.</div>}
      </div>

      <div className="card p-4 h-fit" data-testid="home-hero-form">
        <div className="font-display font-black text-lg uppercase mb-3">{editing ? "Edit banner" : "New banner"}</div>
        <div className="space-y-3">
          <LabeledInput label="Headline" value={form.title} onChange={(v) => setForm(f => ({...f, title: v}))}/>
          <LabeledInput label="Subtitle (eyebrow)" value={form.subtitle} onChange={(v) => setForm(f => ({...f, subtitle: v}))}/>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A1A1AA] font-bold block mb-1">Image</label>
            <input type="url" placeholder="Paste image URL" value={form.image}
              onChange={(e) => setForm(f => ({...f, image: e.target.value}))}
              data-testid="hero-form-image-url"
              className="input-dark w-full px-3 py-2 text-sm mb-2"/>
            <div className="flex items-center gap-2">
              <label className="btn-secondary text-xs cursor-pointer">
                <Upload size={11} className="inline mr-1"/> Upload
                <input type="file" accept="image/*" className="hidden" onChange={onPickFile} data-testid="hero-form-image-file"/>
              </label>
              {form.image && <img src={form.image} alt="" className="w-16 h-12 object-cover rounded border border-[#3D2845]"/>}
            </div>
          </div>
          <LabeledInput label="CTA label" value={form.cta_label} onChange={(v) => setForm(f => ({...f, cta_label: v}))}/>
          <LabeledInput label="CTA href" value={form.cta_href} onChange={(v) => setForm(f => ({...f, cta_href: v}))}/>
          <LabeledInput label="Display order (lower = first)" type="number" value={form.order}
            onChange={(v) => setForm(f => ({...f, order: v}))}/>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.active}
              onChange={(e) => setForm(f => ({...f, active: e.target.checked}))}/>
            Active
          </label>
          <div className="flex gap-2 justify-end pt-2">
            {editing && <button onClick={() => { setEditing(null); setForm(empty); }} className="btn-secondary text-xs">Cancel</button>}
            <button onClick={save} className="btn-primary text-xs" data-testid="hero-save">{editing ? "Update" : "Create"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function HomeMarqueePanel({ flash }) {
  const [lines, setLines] = useState([""]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/site/home-settings");
      setLines(data.marquee_lines && data.marquee_lines.length ? data.marquee_lines : [""]);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const setLine = (i, v) => setLines(ls => ls.map((x, j) => j === i ? v : x));
  const addLine = () => setLines(ls => [...ls, ""]);
  const rmLine = (i) => setLines(ls => ls.length === 1 ? [""] : ls.filter((_, j) => j !== i));
  const moveLine = (i, dir) => {
    setLines(ls => {
      const next = [...ls]; const j = i + dir;
      if (j < 0 || j >= next.length) return next;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };
  const save = async () => {
    setBusy(true);
    try {
      const filtered = lines.map(s => s.trim()).filter(Boolean);
      if (!filtered.length) { alert("Add at least one line."); setBusy(false); return; }
      await api.post(`/admin/site/home-settings`, { marquee_lines: filtered });
      flash("Marquee saved");
    } catch (e) { alert(formatApiError(e?.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-[1fr_360px] gap-4" data-testid="home-marquee-panel">
      <div className="card p-4 space-y-3">
        <div className="font-display font-black uppercase text-base">Marquee lines</div>
        <div className="text-xs text-[#A1A1AA] -mt-2">
          Each line scrolls in the marquee. Use short, punchy phrases (no emoji needed — a star is added automatically).
        </div>
        {lines.map((l, i) => (
          <div key={`marquee-${i}-${l.slice(0, 10)}`} className="flex gap-2">
            <input value={l} onChange={(e) => setLine(i, e.target.value)}
              data-testid={`marquee-line-${i}`}
              placeholder={`Line ${i + 1}`}
              className="input-dark flex-1 px-3 py-2 text-sm uppercase tracking-wide font-bold"/>
            <button onClick={() => moveLine(i, -1)} disabled={i === 0}
              className="btn-secondary text-xs px-2 disabled:opacity-30"><ArrowUp size={12}/></button>
            <button onClick={() => moveLine(i, 1)} disabled={i === lines.length - 1}
              className="btn-secondary text-xs px-2 disabled:opacity-30"><ArrowDown size={12}/></button>
            <button onClick={() => rmLine(i)} className="btn-secondary text-xs px-2 text-[#FF007F]"><Trash2 size={12}/></button>
          </div>
        ))}
        <div className="flex gap-2">
          <button onClick={addLine} data-testid="marquee-add"
            className="btn-secondary text-xs"><Plus size={12} className="inline mr-1"/>Add line</button>
          <button onClick={save} disabled={busy} data-testid="marquee-save"
            className="btn-primary text-xs ml-auto">{busy ? "Saving…" : "Save marquee"}</button>
        </div>
      </div>
      <div className="card p-4 h-fit">
        <div className="text-[10px] uppercase tracking-widest text-[#A1A1AA] font-bold mb-2">Preview</div>
        <div className="overflow-hidden bg-[#D4FF00] border-y-2 border-black py-2 -mx-4">
          <div className="marquee-track">
            {Array.from({length: 4}).map((_, i) => (
              <span key={`marquee-prev-${i}`} className="font-display font-black text-black text-xs uppercase tracking-wider px-6">
                {(lines.filter(Boolean)).map((l, j, arr) => (
                  <span key={`marquee-prev-line-${l}-${j}`}>★ {l}{j < arr.length - 1 ? " · " : "  "}</span>
                ))}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function HomeSectionsPanel({ flash }) {
  const [s, setS] = useState({ sections: {}, categories_first: true });
  const [busy, setBusy] = useState(false);
  const SECTIONS = [
    ["categories", "Shop by category"],
    ["live", "Live now strip"],
    ["rentals", "Rent the look (rentals rail)"],
    ["shopper_strip", "Personalised 'For …' strip"],
    ["trending", "Trending products"],
    ["stores", "Top stores"],
    ["videos", "Shoppable videos"],
  ];
  const load = async () => {
    try {
      const { data } = await api.get("/admin/site/home-settings");
      setS({ sections: data.sections || {}, categories_first: data.categories_first !== false });
    } catch {}
  };
  useEffect(() => { load(); }, []);
  const toggle = (k) => setS(prev => ({ ...prev, sections: { ...prev.sections, [k]: prev.sections[k] === false } }));
  const save = async () => {
    setBusy(true);
    try {
      await api.post("/admin/site/home-settings", {
        sections: s.sections, categories_first: s.categories_first
      });
      flash("Sections saved");
    } catch (e) { alert(formatApiError(e?.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="card p-5 max-w-xl space-y-3" data-testid="home-sections-panel">
      <div className="font-display font-black uppercase text-base">Section visibility</div>
      <div className="text-xs text-[#A1A1AA] -mt-2">
        Toggle off any section you don't want on the home page.
      </div>
      <label className="flex items-center gap-2 p-3 rounded-xl bg-[#1A0F1F] border border-[#D4FF00]/30 mt-2">
        <input type="checkbox" checked={s.categories_first}
          onChange={(e) => setS(prev => ({...prev, categories_first: e.target.checked}))}
          data-testid="home-section-cat-first"/>
        <div className="flex-1">
          <div className="font-bold text-sm">Show "Shop by category" above the hero</div>
          <div className="text-[10px] text-[#A1A1AA]">Recommended ON for marketplace discoverability.</div>
        </div>
      </label>
      <div className="space-y-2 pt-1">
        {SECTIONS.map(([k, l]) => (
          <label key={k} className="flex items-center gap-2 p-3 rounded-xl bg-[#1A0F1F] border border-[#3D2845] hover:border-[#D4FF00]/50">
            <input type="checkbox" checked={s.sections[k] !== false}
              onChange={() => toggle(k)}
              data-testid={`home-section-${k}`}/>
            <span className="text-sm flex-1">{l}</span>
            <span className="text-[10px] text-[#52525B]">{s.sections[k] === false ? "Hidden" : "Visible"}</span>
          </label>
        ))}
      </div>
      <div className="flex justify-end pt-2">
        <button onClick={save} disabled={busy} data-testid="home-sections-save"
          className="btn-primary text-xs">{busy ? "Saving…" : "Save section visibility"}</button>
      </div>
    </div>
  );
}

function HomeCategoriesPanel({ flash }) {
  const ICON_OPTS = ["Package", "Shirt", "Sparkles", "Footprints", "Gem",
                     "Cpu", "Sofa", "Utensils", "Heart", "Baby", "Music",
                     "Gamepad2", "Book", "Camera", "Car", "Plane"];
  const empty = { name: "", icon: "Package", image: "", order: 0, active: true, rentals_eligible: false };
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    try { const { data } = await api.get("/admin/categories"); setRows(data); }
    catch {}
  };
  useEffect(() => { load(); }, []);

  const move = async (i, dir) => {
    const j = i + dir; if (j < 0 || j >= rows.length) return;
    const next = [...rows]; [next[i], next[j]] = [next[j], next[i]];
    setRows(next);
    try {
      await api.post("/admin/categories/reorder", { order: next.map(r => r.category_id) });
      flash("Reordered");
    } catch { load(); }
  };
  const save = async () => {
    if (!form.name.trim()) { alert("Name required"); return; }
    try {
      const payload = { ...form, order: parseInt(form.order) || 0 };
      if (editing) await api.put(`/admin/categories/${editing}`, payload);
      else         await api.post(`/admin/categories`, payload);
      flash(editing ? "Category updated" : "Category created");
      setForm(empty); setEditing(null); load();
    } catch (e) { alert(formatApiError(e?.response?.data?.detail) || "Failed"); }
  };
  const edit = (r) => {
    setEditing(r.category_id);
    setForm({ name: r.name, icon: r.icon || "Package", image: r.image || "",
              order: r.order || 0, active: r.active !== false,
              rentals_eligible: !!r.rentals_eligible });
  };
  const remove = async (id) => {
    if (!window.confirm("Delete category? Products in it will become uncategorised.")) return;
    try { await api.delete(`/admin/categories/${id}`); flash("Deleted"); load(); }
    catch {}
  };
  const toggleActive = async (r) => {
    try {
      await api.put(`/admin/categories/${r.category_id}`, {
        name: r.name, icon: r.icon, image: r.image,
        order: r.order, active: !r.active && r.active !== undefined ? true : !r.active,
        rentals_eligible: !!r.rentals_eligible,
      });
      flash("Updated"); load();
    } catch {}
  };
  const toggleRentEligible = async (r) => {
    try {
      await api.put(`/admin/categories/${r.category_id}`, {
        name: r.name, icon: r.icon, image: r.image,
        order: r.order, active: r.active !== false,
        rentals_eligible: !r.rentals_eligible,
      });
      flash(r.rentals_eligible ? "Rental disabled for category" : "Rental enabled for category");
      load();
    } catch {}
  };
  const onPickFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 1_500_000) { alert("Image too large (max 1.5MB)."); return; }
    const reader = new FileReader();
    reader.onload = () => setForm(s => ({ ...s, image: reader.result }));
    reader.readAsDataURL(f);
  };

  return (
    <div className="grid lg:grid-cols-[1fr_360px] gap-4" data-testid="home-categories-panel">
      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#3D2845] text-left">
            <tr>
              <th className="p-3">#</th>
              <th className="p-3">Category</th>
              <th className="p-3">Icon</th>
              <th className="p-3">Status</th>
              <th className="p-3">Rental</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.category_id} className="border-t border-[#3D2845]" data-testid={`cat-row-${r.category_id}`}>
                <td className="p-3 font-mono-display text-xs text-[#A1A1AA]">{i + 1}</td>
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    {r.image
                      ? <img src={r.image} className="w-10 h-10 rounded object-cover" alt=""/>
                      : <div className="w-10 h-10 rounded bg-[#3D2845]"/>}
                    <span className="font-bold">{r.name}</span>
                  </div>
                </td>
                <td className="p-3 text-xs text-[#A1A1AA] font-mono-display">{r.icon}</td>
                <td className="p-3"><span className={`chip text-[10px] ${r.active === false ? "" : "active"}`}>
                  {r.active === false ? "off" : "on"}
                </span></td>
                <td className="p-3">
                  <button onClick={() => toggleRentEligible(r)}
                    data-testid={`cat-rental-toggle-${r.category_id}`}
                    className={`chip text-[10px] ${r.rentals_eligible ? "active" : ""}`}>
                    {r.rentals_eligible ? "Yes" : "No"}
                  </button>
                </td>
                <td className="p-3 text-right whitespace-nowrap">
                  <button onClick={() => move(i, -1)} disabled={i === 0}
                    className="btn-secondary text-xs px-1.5 py-1 disabled:opacity-30"><ArrowUp size={11}/></button>
                  <button onClick={() => move(i, 1)} disabled={i === rows.length - 1}
                    className="ml-1 btn-secondary text-xs px-1.5 py-1 disabled:opacity-30"><ArrowDown size={11}/></button>
                  <button onClick={() => toggleActive(r)} data-testid={`cat-toggle-${r.category_id}`}
                    className="ml-1 btn-secondary text-xs px-1.5 py-1">
                    {r.active === false ? <PlayCircle size={11}/> : <Pause size={11}/>}
                  </button>
                  <button onClick={() => edit(r)} className="ml-1 btn-secondary text-xs px-1.5 py-1"><Pencil size={11}/></button>
                  <button onClick={() => remove(r.category_id)} className="ml-1 btn-secondary text-xs px-1.5 py-1 text-[#FF007F]"><Trash2 size={11}/></button>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={6} className="p-6 text-center text-[#A1A1AA] text-sm">No categories yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="card p-4 h-fit" data-testid="home-cat-form">
        <div className="font-display font-black text-lg uppercase mb-3">{editing ? "Edit category" : "New category"}</div>
        <div className="space-y-3">
          <LabeledInput label="Name" value={form.name} onChange={(v) => setForm(f => ({...f, name: v}))}/>
          <LabeledSelect label="Icon (lucide-react)" value={form.icon}
            onChange={(v) => setForm(f => ({...f, icon: v}))}
            options={ICON_OPTS.map(i => [i, i])}/>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A1A1AA] font-bold block mb-1">Image</label>
            <input type="url" placeholder="Image URL" value={form.image}
              onChange={(e) => setForm(f => ({...f, image: e.target.value}))}
              className="input-dark w-full px-3 py-2 text-sm mb-2"/>
            <div className="flex items-center gap-2">
              <label className="btn-secondary text-xs cursor-pointer">
                <Upload size={11} className="inline mr-1"/> Upload
                <input type="file" accept="image/*" className="hidden" onChange={onPickFile} data-testid="cat-form-image-file"/>
              </label>
              {form.image && <img src={form.image} alt="" className="w-16 h-12 object-cover rounded border border-[#3D2845]"/>}
            </div>
          </div>
          <LabeledInput label="Order (auto if 0)" type="number" value={form.order}
            onChange={(v) => setForm(f => ({...f, order: v}))}/>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.active}
              onChange={(e) => setForm(f => ({...f, active: e.target.checked}))}/>
            Active (visible to shoppers)
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.rentals_eligible}
              data-testid="cat-form-rentals-eligible"
              onChange={(e) => setForm(f => ({...f, rentals_eligible: e.target.checked}))}/>
            Eligible for rentals (sellers can list items here on rent)
          </label>
          <div className="flex gap-2 justify-end pt-2">
            {editing && <button onClick={() => { setEditing(null); setForm(empty); }} className="btn-secondary text-xs">Cancel</button>}
            <button onClick={save} className="btn-primary text-xs" data-testid="cat-save">{editing ? "Update" : "Create"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Shared small form atoms
function LabeledInput({ label, value, onChange, type = "text" }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-[#A1A1AA] font-bold block mb-1">{label}</label>
      <input type={type} value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="input-dark w-full px-3 py-2 text-sm"/>
    </div>
  );
}
function LabeledSelect({ label, value, onChange, options }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-[#A1A1AA] font-bold block mb-1">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="input-dark w-full px-3 py-2 text-sm">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}


// ======================================================================
// Product Types tab — per-category CRUD taxonomy
// ======================================================================
function AdminProductTypesTab({ flash }) {
  const [cats, setCats] = useState([]);
  const [catId, setCatId] = useState("");
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");

  useEffect(() => {
    api.get("/categories").then(({ data }) => {
      setCats(data || []);
      if (data?.length && !catId) setCatId(data[0].category_id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTypes = async () => {
    if (!catId) return;
    setLoading(true);
    try {
      const { data } = await api.get("/admin/product-types", {
        params: { category_id: catId },
      });
      setTypes(data || []);
    } catch (e) {
      flash(formatApiError(e?.response?.data?.detail) || "Failed to load types", true);
    } finally { setLoading(false); }
  };
  useEffect(() => { if (catId) loadTypes(); /* eslint-disable-next-line */ }, [catId]);

  const addType = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      await api.post("/admin/product-types", {
        category_id: catId, name: newName.trim(),
      });
      setNewName("");
      flash("Product type added.");
      loadTypes();
    } catch (err) {
      flash(formatApiError(err?.response?.data?.detail) || "Add failed", true);
    }
  };

  const saveEdit = async (tid) => {
    try {
      await api.put(`/admin/product-types/${tid}`, { name: editName });
      setEditingId(null);
      flash("Saved.");
      loadTypes();
    } catch (err) {
      flash(formatApiError(err?.response?.data?.detail) || "Save failed", true);
    }
  };

  const toggleActive = async (t) => {
    try {
      await api.put(`/admin/product-types/${t.type_id}`, { active: !t.active });
      loadTypes();
    } catch (err) {
      flash(formatApiError(err?.response?.data?.detail) || "Toggle failed", true);
    }
  };

  const removeType = async (t) => {
    if (!window.confirm(`Delete "${t.name}"? Products currently tagged with it will be un-tagged but kept.`)) return;
    try {
      await api.delete(`/admin/product-types/${t.type_id}`);
      flash("Deleted.");
      loadTypes();
    } catch (err) {
      flash(formatApiError(err?.response?.data?.detail) || "Delete failed", true);
    }
  };

  const reorder = async (fromIdx, toIdx) => {
    if (toIdx < 0 || toIdx >= types.length) return;
    const reordered = [...types];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    setTypes(reordered);  // optimistic
    try {
      await api.post("/admin/product-types/reorder", {
        category_id: catId,
        type_ids: reordered.map(t => t.type_id),
      });
    } catch (err) {
      flash(formatApiError(err?.response?.data?.detail) || "Reorder failed", true);
      loadTypes();  // snap back
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-ptypes-tab">
      <div className="card p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div>
            <div className="font-display font-bold uppercase text-sm">Product types</div>
            <div className="text-xs text-[#A1A1AA] mt-1">
              Manage the sub-categories sellers use when listing (Pant, Shirt, Lipstick, Sneakers…).
              Buyers can filter by these on every category page.
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <label className="text-[10px] font-bold uppercase tracking-widest text-[#A1A1AA]">Category</label>
            <select className="input-dark py-2 text-sm min-w-[200px]"
              value={catId} onChange={e => setCatId(e.target.value)}
              data-testid="ptypes-cat-select">
              {cats.map(c => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
            </select>
          </div>
        </div>
        <form onSubmit={addType} className="flex gap-2 mt-4" data-testid="ptypes-add-form">
          <input className="input-dark flex-1" placeholder="New type (e.g. Pant, T-shirt, Serum)"
            value={newName} onChange={e => setNewName(e.target.value)}
            data-testid="ptypes-add-input"/>
          <button type="submit" className="btn-primary text-xs px-4"
            data-testid="ptypes-add-btn">
            <Plus size={14} className="inline mr-1"/> Add type
          </button>
        </form>
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full text-sm" data-testid="ptypes-table">
          <thead className="bg-[#3D2845] text-left">
            <tr className="text-xs uppercase tracking-widest font-bold text-[#A1A1AA]">
              <th className="p-3">#</th>
              <th className="p-3">Name</th>
              <th className="p-3">Slug</th>
              <th className="p-3">Status</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="p-8 text-center text-[#A1A1AA]">Loading…</td></tr>
            )}
            {!loading && types.length === 0 && (
              <tr><td colSpan={5} className="p-8 text-center text-[#A1A1AA]">No product types yet. Add the first one above.</td></tr>
            )}
            {!loading && types.map((t, i) => (
              <tr key={t.type_id} className="border-t border-[#3D2845]"
                data-testid={`ptypes-row-${t.type_id}`}>
                <td className="p-3 text-xs text-[#A1A1AA] font-mono-display">{i + 1}</td>
                <td className="p-3">
                  {editingId === t.type_id ? (
                    <input className="input-dark py-1 min-w-[200px]"
                      value={editName} onChange={e => setEditName(e.target.value)}
                      data-testid={`ptypes-edit-input-${t.type_id}`} autoFocus/>
                  ) : (
                    <div className="font-bold">{t.name}</div>
                  )}
                </td>
                <td className="p-3 text-[10px] font-mono-display text-[#A1A1AA]">{t.slug}</td>
                <td className="p-3">
                  <button onClick={() => toggleActive(t)}
                    data-testid={`ptypes-toggle-${t.type_id}`}
                    className={`text-[10px] uppercase font-bold tracking-widest px-2 py-1 rounded ${
                      t.active ? "bg-[#D4FF00]/15 text-[#D4FF00]" : "bg-[#A1A1AA]/15 text-[#A1A1AA]"
                    }`}>
                    {t.active ? "Active" : "Paused"}
                  </button>
                </td>
                <td className="p-3 text-right">
                  <div className="flex gap-1 justify-end items-center">
                    <button onClick={() => reorder(i, i - 1)} title="Move up"
                      disabled={i === 0}
                      className="w-7 h-7 rounded-lg bg-[#3D2845] hover:bg-[#3D2845]/80 disabled:opacity-30 flex items-center justify-center">
                      <ArrowUp size={12}/>
                    </button>
                    <button onClick={() => reorder(i, i + 1)} title="Move down"
                      disabled={i === types.length - 1}
                      className="w-7 h-7 rounded-lg bg-[#3D2845] hover:bg-[#3D2845]/80 disabled:opacity-30 flex items-center justify-center">
                      <ArrowDown size={12}/>
                    </button>
                    {editingId === t.type_id ? (
                      <>
                        <button onClick={() => saveEdit(t.type_id)} title="Save"
                          data-testid={`ptypes-save-${t.type_id}`}
                          className="w-7 h-7 rounded-lg bg-[#D4FF00] text-black flex items-center justify-center">
                          <Check size={12}/>
                        </button>
                        <button onClick={() => setEditingId(null)} title="Cancel"
                          className="w-7 h-7 rounded-lg bg-[#3D2845] hover:bg-[#FF007F] hover:text-white flex items-center justify-center">
                          <X size={12}/>
                        </button>
                      </>
                    ) : (
                      <button onClick={() => { setEditingId(t.type_id); setEditName(t.name); }}
                        data-testid={`ptypes-edit-${t.type_id}`}
                        title="Edit"
                        className="w-7 h-7 rounded-lg bg-[#3D2845] hover:bg-[#D4FF00] hover:text-black flex items-center justify-center">
                        <Pencil size={12}/>
                      </button>
                    )}
                    <button onClick={() => removeType(t)} title="Delete"
                      data-testid={`ptypes-del-${t.type_id}`}
                      className="w-7 h-7 rounded-lg bg-[#3D2845] hover:bg-[#FF007F] hover:text-white flex items-center justify-center">
                      <Trash2 size={12}/>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-xs text-[#A1A1AA] text-right">
        {types.length} type{types.length === 1 ? "" : "s"} in this category.
      </div>
    </div>
  );
}
